import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import {
    Search, Plus, Minus, X, ShoppingCart, CreditCard, Banknote,
    Smartphone, Check, Package, Stethoscope, Syringe,
    Apple, Sparkles, ChevronRight, ReceiptText, Trash2, User,
    Tag, History, Printer, Share2, Edit2, FileText, Settings,
    Save, BarChart2, DollarSign, AlertCircle, RefreshCw,
    TrendingUp, TrendingDown, Wallet, ArrowUpRight, ArrowDownRight,
    Terminal, Wifi, CheckCircle, Clock, Lock
} from 'lucide-react';
import { api } from '../../../services/api';

/* ─── TYPES ─── */
type Category = 'todo' | 'servicios' | 'medicamentos' | 'vacunas' | 'alimentos' | 'higiene' | 'accesorios' | 'hospitalario';
type PayMethod = 'efectivo' | 'debito' | 'credito' | 'transferencia';
type DocType   = 'BOLETA' | 'FACTURA';
type POSView   = 'pos' | 'manage' | 'caja' | 'reportes';
type TermProvider = 'tuu' | 'mp' | 'transbank';
type TermState = 'idle' | 'requesting' | 'waiting' | 'confirmed' | 'error';

interface Product {
    id: string; name: string; price: number; category: Category;
    stock: number; unit: string; sku: string;
    stock_alert_threshold?: number;
    description?: string;
}
interface CartItem extends Product { qty: number; }
interface Sale {
    id: string; saleNumber: string; items: CartItem[];
    subtotal: number; discountPct: number; discountAmt: number; total: number;
    payment: PayMethod; cashReceived?: number; changeGiven?: number;
    docType: DocType; clientName?: string; clientRut?: string;
    terminalProvider?: TermProvider; terminalResponse?: any;
    date: string; time: string; status: 'completed' | 'pending' | 'cancelled';
}
interface CajaSession {
    id: string; date: string; openedAt: string; closedAt?: string;
    startAmount: number; endAmount?: number; status: 'open' | 'closed';
}
interface TerminalConfig {
    tuu:       { api_key: string; terminal_id: string };
    mp:        { access_token: string; terminal_id: string };
    transbank: { commerce_code: string };
}

/* ─── CONSTANTS ─── */
const CATEGORIES: { id: Category; label: string; icon: React.ElementType; color: string; bg: string }[] = [
    { id: 'todo',         label: 'Todo',          icon: Package,     color: 'text-slate-500',   bg: 'bg-slate-100'   },
    { id: 'servicios',    label: 'Servicios',     icon: Stethoscope, color: 'text-indigo-600',  bg: 'bg-indigo-50'   },
    { id: 'vacunas',      label: 'Vacunas',       icon: Syringe,     color: 'text-emerald-600', bg: 'bg-emerald-50'  },
    { id: 'medicamentos', label: 'Medicamentos',  icon: Sparkles,    color: 'text-violet-600',  bg: 'bg-violet-50'   },
    { id: 'hospitalario', label: 'Hospitalario',  icon: Syringe,     color: 'text-rose-600',    bg: 'bg-rose-50'     },
    { id: 'alimentos',    label: 'Alimentos',     icon: Apple,       color: 'text-orange-600',  bg: 'bg-orange-50'   },
    { id: 'higiene',      label: 'Higiene',       icon: Sparkles,    color: 'text-sky-600',     bg: 'bg-sky-50'      },
    { id: 'accesorios',   label: 'Accesorios',    icon: Tag,         color: 'text-rose-600',    bg: 'bg-rose-50'     },
];
const PAY_METHODS: { id: PayMethod; label: string; icon: React.ElementType; color: string }[] = [
    { id: 'efectivo',      label: 'Efectivo',      icon: Banknote,   color: 'bg-emerald-500' },
    { id: 'debito',        label: 'Débito',        icon: CreditCard, color: 'bg-[#1E3A8A]'  },
    { id: 'credito',       label: 'Crédito',       icon: CreditCard, color: 'bg-violet-600'  },
    { id: 'transferencia', label: 'Transferencia', icon: Smartphone, color: 'bg-orange-500'  },
];
const INITIAL_PRODUCTS: Product[] = [
    /* ── SERVICIOS ── */
    { id: 's1',  name: 'Consulta General',            price: 15000, category: 'servicios',    stock: 99, unit: 'servicio', sku: 'SRV-001' },
    { id: 's2',  name: 'Consulta Urgencia',            price: 28000, category: 'servicios',    stock: 99, unit: 'servicio', sku: 'SRV-002' },
    { id: 's3',  name: 'Control Postoperatorio',       price: 12000, category: 'servicios',    stock: 99, unit: 'servicio', sku: 'SRV-003' },
    { id: 's4',  name: 'Examen Sangre Completo',       price: 22000, category: 'servicios',    stock: 99, unit: 'examen',   sku: 'SRV-004' },
    { id: 's5',  name: 'Radiografía Digital',          price: 28000, category: 'servicios',    stock: 99, unit: 'placa',    sku: 'SRV-005' },
    { id: 's6',  name: 'Ecografía Abdominal',          price: 32000, category: 'servicios',    stock: 99, unit: 'sesión',   sku: 'SRV-006' },
    { id: 's7',  name: 'Cirugía Esterilización Hembra',price: 75000, category: 'servicios',    stock: 99, unit: 'proccd.',  sku: 'SRV-007' },
    { id: 's8',  name: 'Cirugía Esterilización Macho', price: 45000, category: 'servicios',    stock: 99, unit: 'proccd.',  sku: 'SRV-008' },
    { id: 's9',  name: 'Limpieza Dental Ultrasónica',  price: 35000, category: 'servicios',    stock: 99, unit: 'sesión',   sku: 'SRV-009' },
    { id: 's10', name: 'Hospitalización Día',          price: 18000, category: 'servicios',    stock: 99, unit: 'día',      sku: 'SRV-010' },
    /* ── VACUNAS ── */
    { id: 'v1',  name: 'Vacuna Séxtuple Canina',       price: 18500, category: 'vacunas',      stock: 12, unit: 'dosis',    sku: 'VAC-001', stock_alert_threshold: 3 },
    { id: 'v2',  name: 'Vacuna Antirrábica',           price: 12000, category: 'vacunas',      stock: 8,  unit: 'dosis',    sku: 'VAC-002', stock_alert_threshold: 3 },
    { id: 'v3',  name: 'Vacuna Triple Felina (HCPi)',  price: 14500, category: 'vacunas',      stock: 6,  unit: 'dosis',    sku: 'VAC-003', stock_alert_threshold: 2 },
    { id: 'v4',  name: 'Vacuna Leucemia Felina (FeLV)',price: 16000, category: 'vacunas',      stock: 4,  unit: 'dosis',    sku: 'VAC-004', stock_alert_threshold: 2 },
    { id: 'v5',  name: 'Vacuna Bordetella Canina',     price: 15500, category: 'vacunas',      stock: 6,  unit: 'dosis',    sku: 'VAC-005', stock_alert_threshold: 2 },
    { id: 'v6',  name: 'Vacuna Leptospira 4-valente',  price: 14000, category: 'vacunas',      stock: 8,  unit: 'dosis',    sku: 'VAC-006', stock_alert_threshold: 2 },
    /* ── ANTIBIÓTICOS ── */
    { id: 'a01', name: 'Amoxicilina 500mg x 14 cp',           price:  8500, category: 'medicamentos', stock: 20, unit: 'caja',    sku: 'MED-A01', stock_alert_threshold: 5, description: 'Dosis: 10-20 mg/kg c/12h PO. Perros y gatos.' },
    { id: 'a02', name: 'Amoxicilina-Clavulanato 500/125mg x14',price:13500, category: 'medicamentos', stock: 15, unit: 'caja',    sku: 'MED-A02', stock_alert_threshold: 5, description: 'Dosis: 12.5 mg/kg c/12h PO. Infecciones con resistencia.' },
    { id: 'a03', name: 'Cefalexina 500mg x 14 cp',            price:  9500, category: 'medicamentos', stock: 18, unit: 'caja',    sku: 'MED-A03', stock_alert_threshold: 5, description: 'Dosis: 15-30 mg/kg c/12h PO. Piel, tejidos blandos.' },
    { id: 'a04', name: 'Enrofloxacina 50mg x 10 cp',          price: 12000, category: 'medicamentos', stock: 12, unit: 'caja',    sku: 'MED-A04', stock_alert_threshold: 3, description: 'Dosis: 5-10 mg/kg c/24h PO. NO usar en gatos >5 días.' },
    { id: 'a05', name: 'Enrofloxacina 50mg/ml 10ml iny',      price: 10500, category: 'medicamentos', stock: 10, unit: 'frasco',  sku: 'MED-A05', stock_alert_threshold: 3, description: 'Dosis: 5 mg/kg c/24h SC/IM. Bacterias gram-neg.' },
    { id: 'a06', name: 'Doxiciclina 100mg x 14 cp',           price:  8500, category: 'medicamentos', stock: 15, unit: 'caja',    sku: 'MED-A06', stock_alert_threshold: 4, description: 'Dosis: 5-10 mg/kg c/12h PO. Rickettsias, Mycoplasma.' },
    { id: 'a07', name: 'Metronidazol 250mg x 20 cp',          price:  5500, category: 'medicamentos', stock: 20, unit: 'caja',    sku: 'MED-A07', stock_alert_threshold: 5, description: 'Dosis: 10-15 mg/kg c/12h PO. Anaerobios, Giardia.' },
    { id: 'a08', name: 'Metronidazol 500mg/100ml IV 100ml',   price:  7500, category: 'medicamentos', stock: 12, unit: 'bolsa',   sku: 'MED-A08', stock_alert_threshold: 4, description: 'Dosis: 10-15 mg/kg c/12h IV lento 60 min. Anaerobios.' },
    { id: 'a09', name: 'Clindamicina 300mg x 16 cp',          price: 14500, category: 'medicamentos', stock: 10, unit: 'caja',    sku: 'MED-A09', stock_alert_threshold: 3, description: 'Dosis: 5-11 mg/kg c/12h PO. Infecciones óseas, dentales.' },
    { id: 'a10', name: 'Trimetoprim/Sulfa 480mg x 14 cp',     price:  6500, category: 'medicamentos', stock: 15, unit: 'caja',    sku: 'MED-A10', stock_alert_threshold: 4, description: 'Dosis: 15-30 mg/kg c/12h PO. ITU, infecciones respiratorias.' },
    { id: 'a11', name: 'Marbofloxacina 20mg x 7 cp',          price: 19500, category: 'medicamentos', stock: 8,  unit: 'caja',    sku: 'MED-A11', stock_alert_threshold: 2, description: 'Dosis: 2-5.5 mg/kg c/24h PO. ITU, piel, respiratorio.' },
    { id: 'a12', name: 'Azitromicina 250mg x 6 cp',           price: 11500, category: 'medicamentos', stock: 10, unit: 'caja',    sku: 'MED-A12', stock_alert_threshold: 3, description: 'Dosis: 5-10 mg/kg c/24h PO x 5 días. Respiratorio, Toxoplasma.' },
    /* ── ANTIINFLAMATORIOS / ANALGÉSICOS ── */
    { id: 'ai01', name: 'Meloxicam 1.5mg/ml 15ml sol oral',    price:  8500, category: 'medicamentos', stock: 20, unit: 'frasco',  sku: 'MED-AI01', stock_alert_threshold: 5, description: 'Dosis mantenimiento: 0.1 mg/kg c/24h PO. AINE de elección.' },
    { id: 'ai02', name: 'Meloxicam 5mg/ml 10ml iny',           price:  9500, category: 'medicamentos', stock: 12, unit: 'frasco',  sku: 'MED-AI02', stock_alert_threshold: 4, description: 'Dosis inicial: 0.2 mg/kg SC/IM. Luego 0.1 mg/kg/día PO.' },
    { id: 'ai03', name: 'Carprofeno 50mg x 20 cp',             price: 22000, category: 'medicamentos', stock: 10, unit: 'caja',    sku: 'MED-AI03', stock_alert_threshold: 3, description: 'Dosis: 2.2 mg/kg c/12h o 4.4 mg/kg c/24h PO. Solo perros.' },
    { id: 'ai04', name: 'Carprofeno 50mg/ml 20ml iny',         price: 18500, category: 'medicamentos', stock: 8,  unit: 'frasco',  sku: 'MED-AI04', stock_alert_threshold: 2, description: 'Dosis: 4 mg/kg SC/IV una vez. Solo perros. Perioperatorio.' },
    { id: 'ai05', name: 'Tramadol HCl 50mg x 20 cp',           price:  9500, category: 'medicamentos', stock: 15, unit: 'caja',    sku: 'MED-AI05', stock_alert_threshold: 4, description: 'Dosis perro: 2-5 mg/kg c/8h. Gato: 1-2 mg/kg c/12h. Opiáceo.' },
    { id: 'ai06', name: 'Tramadol 50mg/ml 2ml iny',            price: 12000, category: 'medicamentos', stock: 10, unit: 'ampolla', sku: 'MED-AI06', stock_alert_threshold: 3, description: 'Dosis: 2-5 mg/kg SC/IM c/8h. Dolor moderado-severo.' },
    { id: 'ai07', name: 'Buprenorfina 0.3mg/ml 1ml iny',       price:  9500, category: 'medicamentos', stock: 8,  unit: 'ampolla', sku: 'MED-AI07', stock_alert_threshold: 2, description: 'Dosis: 0.01-0.03 mg/kg IV/IM/SC c/6-8h. Opiáceo parcial.' },
    { id: 'ai08', name: 'Dipirona 500mg/ml 10ml iny',          price:  5000, category: 'medicamentos', stock: 20, unit: 'frasco',  sku: 'MED-AI08', stock_alert_threshold: 5, description: 'Dosis: 25-35 mg/kg c/8h IV/SC. Antipirético, analgésico.' },
    { id: 'ai09', name: 'Ketoprofeno 100mg/ml 10ml iny',       price:  7500, category: 'medicamentos', stock: 10, unit: 'frasco',  sku: 'MED-AI09', stock_alert_threshold: 3, description: 'Dosis: 1-2 mg/kg c/24h SC/IM. Solo 3-5 días máximo.' },
    { id: 'ai10', name: 'Gabapentina 100mg x 30 cp',           price: 10500, category: 'medicamentos', stock: 12, unit: 'caja',    sku: 'MED-AI10', stock_alert_threshold: 3, description: 'Dosis perro: 5-10 mg/kg c/12h. Gato: 5-10 mg/kg c/12h. Dolor neuropático.' },
    /* ── ANTIPARASITARIOS ── */
    { id: 'ap01', name: 'Ivermectina 1% 50ml iny',             price:  6500, category: 'medicamentos', stock: 15, unit: 'frasco',  sku: 'MED-AP01', stock_alert_threshold: 4, description: 'Dosis: 0.2-0.4 mg/kg SC. NO en Collies/razas MDR1+.' },
    { id: 'ap02', name: 'Fenbendazol 250mg/ml 100ml susp',     price:  8000, category: 'medicamentos', stock: 12, unit: 'frasco',  sku: 'MED-AP02', stock_alert_threshold: 3, description: 'Dosis: 50 mg/kg c/24h PO x 3-5 días. Nematodos, Giardia.' },
    { id: 'ap03', name: 'Prazicuantel 50mg/ml 10ml iny',       price:  9500, category: 'medicamentos', stock: 10, unit: 'frasco',  sku: 'MED-AP03', stock_alert_threshold: 3, description: 'Dosis: 5-10 mg/kg SC. Cestodos (tenias), Echinococcus.' },
    { id: 'ap04', name: 'Milbemicina Oxima 16mg x 4 cp',       price: 18500, category: 'medicamentos', stock: 8,  unit: 'caja',    sku: 'MED-AP04', stock_alert_threshold: 2, description: 'Dosis: 0.5 mg/kg c/30 días PO. Dirofilaria, nemátodos.' },
    { id: 'ap05', name: 'Selamectina 240mg pipeta (>40kg)',     price: 14500, category: 'medicamentos', stock: 8,  unit: 'pipeta',  sku: 'MED-AP05', stock_alert_threshold: 2, description: 'Dosis: 6 mg/kg tópico mensual. Pulgas, sarcoptes, otodectes.' },
    /* ── ANTIEMÉTICO / GI ── */
    { id: 'gi01', name: 'Maropitant 16mg x 4 cp (Cerenia)',    price: 23500, category: 'medicamentos', stock: 12, unit: 'caja',    sku: 'MED-GI01', stock_alert_threshold: 3, description: 'Dosis: 2 mg/kg c/24h PO. Anti-NK1. Vómito agudo y crónico.' },
    { id: 'gi02', name: 'Maropitant 10mg/ml 20ml iny',         price: 29500, category: 'medicamentos', stock: 8,  unit: 'frasco',  sku: 'MED-GI02', stock_alert_threshold: 2, description: 'Dosis: 1 mg/kg SC c/24h. Cinetosis, náuseas perioperatorias.' },
    { id: 'gi03', name: 'Metoclopramida 5mg/ml 10ml iny',      price:  4500, category: 'medicamentos', stock: 15, unit: 'frasco',  sku: 'MED-GI03', stock_alert_threshold: 4, description: 'Dosis: 0.2-0.5 mg/kg c/6-8h IV/SC. Procinético, antiemético.' },
    { id: 'gi04', name: 'Ondansetron 2mg/ml 4ml iny',          price:  8500, category: 'medicamentos', stock: 10, unit: 'ampolla', sku: 'MED-GI04', stock_alert_threshold: 3, description: 'Dosis: 0.1-0.15 mg/kg IV lento c/8-12h. Antiemético 5-HT3.' },
    { id: 'gi05', name: 'Omeprazol 20mg x 14 cp',              price:  5500, category: 'medicamentos', stock: 20, unit: 'caja',    sku: 'MED-GI05', stock_alert_threshold: 5, description: 'Dosis: 0.5-1 mg/kg c/24h PO. Gastroprotector, úlceras.' },
    { id: 'gi06', name: 'Famotidina 10mg/ml 2ml iny',          price:  6000, category: 'medicamentos', stock: 12, unit: 'ampolla', sku: 'MED-GI06', stock_alert_threshold: 3, description: 'Dosis: 0.5-1 mg/kg c/12-24h IV/SC. Antiácido H2.' },
    { id: 'gi07', name: 'Sucralfato 200mg/ml 200ml susp',      price:  9500, category: 'medicamentos', stock: 8,  unit: 'frasco',  sku: 'MED-GI07', stock_alert_threshold: 2, description: 'Dosis: 0.5-1 g c/8h PO. Protector mucosa gástrica. Dar separado de otros meds.' },
    /* ── CORTICOIDES ── */
    { id: 'co01', name: 'Prednisolona 5mg x 20 cp',            price:  6000, category: 'medicamentos', stock: 20, unit: 'caja',    sku: 'MED-CO01', stock_alert_threshold: 5, description: 'Dosis inmunosupresora: 1-2 mg/kg/día. Antiinflamatoria: 0.5 mg/kg/día.' },
    { id: 'co02', name: 'Dexametasona 4mg/ml 10ml iny',        price:  5000, category: 'medicamentos', stock: 15, unit: 'frasco',  sku: 'MED-CO02', stock_alert_threshold: 4, description: 'Dosis: 0.1-0.5 mg/kg IV/IM. Shock, edema cerebral, alergia severa.' },
    { id: 'co03', name: 'Metilprednisolona 40mg/ml 1ml iny',   price: 13500, category: 'medicamentos', stock: 8,  unit: 'ampolla', sku: 'MED-CO03', stock_alert_threshold: 2, description: 'Dosis: 1-2 mg/kg IV. Crisis alérgica severa, shock anafiláctico.' },
    /* ── CARDIOVASCULAR / DIURÉTICO ── */
    { id: 'cv01', name: 'Furosemida 10mg/ml 10ml iny',         price:  4500, category: 'medicamentos', stock: 15, unit: 'frasco',  sku: 'MED-CV01', stock_alert_threshold: 4, description: 'Dosis: 1-4 mg/kg IV/IM c/4-8h. Edema pulmonar agudo, ICC.' },
    { id: 'cv02', name: 'Furosemida 40mg x 20 cp',             price:  4000, category: 'medicamentos', stock: 18, unit: 'caja',    sku: 'MED-CV02', stock_alert_threshold: 5, description: 'Dosis: 1-4 mg/kg c/8-12h PO. Edema crónico, ICC.' },
    { id: 'cv03', name: 'Enalapril 5mg x 20 cp',               price:  7500, category: 'medicamentos', stock: 15, unit: 'caja',    sku: 'MED-CV03', stock_alert_threshold: 4, description: 'Dosis: 0.5 mg/kg c/12-24h PO. IECA. Cardiopatía, proteinuria.' },
    { id: 'cv04', name: 'Pimobendan 1.25mg x 50 cp (Vetmedin)',price: 28500, category: 'medicamentos', stock: 6,  unit: 'caja',    sku: 'MED-CV04', stock_alert_threshold: 2, description: 'Dosis: 0.1-0.3 mg/kg c/12h PO (30 min antes de comer). MVD, DCM.' },
    { id: 'cv05', name: 'Espironolactona 25mg x 20 cp',        price:  6500, category: 'medicamentos', stock: 12, unit: 'caja',    sku: 'MED-CV05', stock_alert_threshold: 3, description: 'Dosis: 2 mg/kg c/24h PO. Diurético ahorrador de potasio. ICC.' },
    { id: 'cv06', name: 'Atenolol 25mg x 30 cp',               price:  5500, category: 'medicamentos', stock: 10, unit: 'caja',    sku: 'MED-CV06', stock_alert_threshold: 3, description: 'Dosis: 0.25-1 mg/kg c/12h PO. Betabloqueante. Taquiarritmias.' },
    /* ── ANESTÉSICOS / SEDANTES ── */
    { id: 'an01', name: 'Ketamina 10% 10ml iny',               price: 12500, category: 'medicamentos', stock: 8,  unit: 'frasco',  sku: 'MED-AN01', stock_alert_threshold: 2, description: 'Dosis inducción: 5-10 mg/kg IM. Anestesia disociativa. Siempre combinar con BZD.' },
    { id: 'an02', name: 'Propofol 10mg/ml 20ml iny',           price: 19500, category: 'medicamentos', stock: 6,  unit: 'ampolla', sku: 'MED-AN02', stock_alert_threshold: 2, description: 'Dosis inducción: 4-6 mg/kg IV lento (titulado). Anestesia total IV.' },
    { id: 'an03', name: 'Acepromazina 1% 10ml iny',            price:  6000, category: 'medicamentos', stock: 10, unit: 'frasco',  sku: 'MED-AN03', stock_alert_threshold: 3, description: 'Dosis: 0.05-0.1 mg/kg IM/SC. Preanestésico fenotiazínico. NO en epilépticos.' },
    { id: 'an04', name: 'Dexmedetomidina 0.5mg/ml 4ml iny',    price: 22500, category: 'medicamentos', stock: 6,  unit: 'ampolla', sku: 'MED-AN04', stock_alert_threshold: 2, description: 'Dosis: 5-20 mcg/kg IM. Sedación profunda, alfa-2 agonista. Reversible con atipamezol.' },
    { id: 'an05', name: 'Midazolam 5mg/ml 3ml iny',            price:  8500, category: 'medicamentos', stock: 8,  unit: 'ampolla', sku: 'MED-AN05', stock_alert_threshold: 2, description: 'Dosis: 0.2-0.5 mg/kg IM/IV. Benzodiacepina. Relajante muscular, anticonvulsivante.' },
    { id: 'an06', name: 'Butorfanol 10mg/ml 10ml iny',         price: 24500, category: 'medicamentos', stock: 6,  unit: 'frasco',  sku: 'MED-AN06', stock_alert_threshold: 2, description: 'Dosis: 0.2-0.4 mg/kg IV/IM. Opioide agonista-antagonista. Antitusivo.' },
    { id: 'an07', name: 'Atropina Sulfato 0.5mg/ml 1ml iny',   price:  3500, category: 'medicamentos', stock: 15, unit: 'ampolla', sku: 'MED-AN07', stock_alert_threshold: 5, description: 'Dosis: 0.02-0.04 mg/kg IV/IM/SC. Anticolinérgico. Bradicardia, preanestesia.' },
    { id: 'an08', name: 'Isoflurano 100% liq 250ml',           price: 45000, category: 'medicamentos', stock: 4,  unit: 'frasco',  sku: 'MED-AN08', stock_alert_threshold: 1, description: 'CAM perro: 1.28%, gato: 1.63%. Anestesia por inhalación. Uso con vaporímetro calibrado.' },
    /* ── ANTIMICÓTICOS ── */
    { id: 'af01', name: 'Ketoconazol 200mg x 14 cp',           price:  8500, category: 'medicamentos', stock: 10, unit: 'caja',    sku: 'MED-AF01', stock_alert_threshold: 3, description: 'Dosis: 5-10 mg/kg c/24h PO. Dermatofitos, Malassezia. Control hepático.' },
    { id: 'af02', name: 'Fluconazol 50mg x 7 cp',              price: 11500, category: 'medicamentos', stock: 8,  unit: 'caja',    sku: 'MED-AF02', stock_alert_threshold: 2, description: 'Dosis: 5-10 mg/kg c/24h PO. Criptococcus, Candida, dermatofitos.' },
    { id: 'af03', name: 'Itraconazol 100mg x 15 cp',           price: 19500, category: 'medicamentos', stock: 6,  unit: 'caja',    sku: 'MED-AF03', stock_alert_threshold: 2, description: 'Dosis: 5 mg/kg c/24h PO. Aspergillus, Sporothrix, Histoplasma.' },
    /* ── SUEROS / FLUIDOS IV — HOSPITALARIO ── */
    { id: 'h01', name: 'NaCl 0.9% Suero Fisiológico 500ml',   price:  3200, category: 'hospitalario', stock: 30, unit: 'bolsa',   sku: 'HSP-H01', stock_alert_threshold: 8, description: 'Mantenimiento: 40-60 ml/kg/día IV. Reposición de Na+. Isoosmótico.' },
    { id: 'h02', name: 'NaCl 0.9% Suero Fisiológico 1000ml',  price:  4500, category: 'hospitalario', stock: 20, unit: 'bolsa',   sku: 'HSP-H02', stock_alert_threshold: 5, description: 'Mantenimiento: 40-60 ml/kg/día IV. Reposición de Na+. Isoosmótico.' },
    { id: 'h03', name: 'Ringer Lactato 500ml',                 price:  3800, category: 'hospitalario', stock: 25, unit: 'bolsa',   sku: 'HSP-H03', stock_alert_threshold: 8, description: 'Mantenimiento: 40-60 ml/kg/día IV. Solución balanceada. Acidosis metabólica.' },
    { id: 'h04', name: 'Ringer Lactato 1000ml',                price:  5200, category: 'hospitalario', stock: 15, unit: 'bolsa',   sku: 'HSP-H04', stock_alert_threshold: 5, description: 'Mantenimiento: 40-60 ml/kg/día IV. Solución balanceada. Acidosis metabólica.' },
    { id: 'h05', name: 'Dextrosa 5% en Agua 500ml',            price:  3600, category: 'hospitalario', stock: 20, unit: 'bolsa',   sku: 'HSP-H05', stock_alert_threshold: 5, description: 'Hipoglicemia, energía, dilución fármacos. NO usar sola para reposición de volumen.' },
    { id: 'h06', name: 'Dextrosa 50% 500ml',                   price:  6500, category: 'hospitalario', stock: 10, unit: 'frasco',  sku: 'HSP-H06', stock_alert_threshold: 3, description: 'Corrección hipoglicemia severa: 0.5-1 ml/kg IV diluido. Siempre monitorear glucemia.' },
    { id: 'h07', name: 'NaCl 20% (Hipertónico) 50ml iny',     price:  4500, category: 'hospitalario', stock: 10, unit: 'ampolla', sku: 'HSP-H07', stock_alert_threshold: 3, description: 'Corrección hiponatremia: máx 0.5 mEq/L/h. Siempre diluir. Monitoreo electrolítico.' },
    { id: 'h08', name: 'KCl 20% (Cloruro Potasio) 20ml iny',  price:  3800, category: 'hospitalario', stock: 12, unit: 'ampolla', sku: 'HSP-H08', stock_alert_threshold: 4, description: 'Suplemento: máx 0.5 mEq/kg/h IV. NUNCA IV directo. Siempre diluir en suero.' },
    { id: 'h09', name: 'Plasma Expander (Dextrano 70) 500ml',  price: 24500, category: 'hospitalario', stock: 6,  unit: 'bolsa',   sku: 'HSP-H09', stock_alert_threshold: 2, description: 'Dosis: 10-20 ml/kg IV. Shock hipovolémico, hipoalbuminemia. Expansor oncótico.' },
    { id: 'h10', name: 'Equipo venoclisis con cámara 60 gotas',price:  1800, category: 'hospitalario', stock: 30, unit: 'unid.',   sku: 'HSP-H10', stock_alert_threshold: 10 },
    { id: 'h11', name: 'Catéter IV 18G (perros grandes) x 5', price:  7500, category: 'hospitalario', stock: 20, unit: 'caja',    sku: 'HSP-H11', stock_alert_threshold: 5, description: 'Calibre estándar para fluidoterapia en perros medianos/grandes.' },
    { id: 'h12', name: 'Catéter IV 22G (perros peq/gatos) x5',price:  7500, category: 'hospitalario', stock: 20, unit: 'caja',    sku: 'HSP-H12', stock_alert_threshold: 5, description: 'Calibre para gatos y perros pequeños.' },
    { id: 'h13', name: 'Catéter IV 24G (neonatos) x 5',       price:  8500, category: 'hospitalario', stock: 10, unit: 'caja',    sku: 'HSP-H13', stock_alert_threshold: 3, description: 'Calibre para neonatos y venas muy finas.' },
    /* ── ALIMENTOS ── */
    { id: 'al1', name: 'Royal Canin Adult Medium 2kg',         price: 22000, category: 'alimentos',    stock: 10, unit: 'bolsa',   sku: 'ALI-001' },
    { id: 'al2', name: 'Pedigree Cachorro 1kg',                price:  8900, category: 'alimentos',    stock: 15, unit: 'bolsa',   sku: 'ALI-002' },
    { id: 'al3', name: 'Hills Science Diet Cat 1.5kg',         price: 19500, category: 'alimentos',    stock: 8,  unit: 'bolsa',   sku: 'ALI-003' },
    { id: 'al4', name: 'Royal Canin Recovery (lat 400g)',      price: 9800,  category: 'alimentos',    stock: 12, unit: 'lata',    sku: 'ALI-004', description: 'Dieta hipercalórica para recuperación post-cirugía u hospitalización.' },
    { id: 'al5', name: 'Hills a/d (lat 156g)',                 price: 6500,  category: 'alimentos',    stock: 15, unit: 'lata',    sku: 'ALI-005', description: 'Dieta de recuperación. Alta digestibilidad. Ideal para pacientes hospitalizados.' },
    /* ── HIGIENE ── */
    { id: 'hi1', name: 'Frontline Plus Perro S (2-10kg)',      price: 14500, category: 'higiene',      stock: 7,  unit: 'pipeta',  sku: 'HIG-001' },
    { id: 'hi2', name: 'Shampoo Medicado Clorhexidina 250ml',  price: 9800,  category: 'higiene',      stock: 12, unit: 'frasco',  sku: 'HIG-002' },
    { id: 'hi3', name: 'Collar Antipulgas Seresto 45cm',       price: 22500, category: 'higiene',      stock: 8,  unit: 'unid.',   sku: 'HIG-003' },
    { id: 'hi4', name: 'Clorhexidina 2% Solución 500ml',       price: 5500,  category: 'higiene',      stock: 15, unit: 'frasco',  sku: 'HIG-004', description: 'Antiséptico tópico. Lavado de heridas, limpieza pre-quirúrgica.' },
    { id: 'hi5', name: 'Yodo Povidona 10% 500ml',              price: 4500,  category: 'higiene',      stock: 12, unit: 'frasco',  sku: 'HIG-005', description: 'Antiséptico iodado. Preparación campo quirúrgico, limpieza de heridas.' },
    /* ── ACCESORIOS / INSUMOS ── */
    { id: 'ac1', name: 'Guantes Nitrilo S/M/L x100',           price: 12800, category: 'accesorios',   stock: 5,  unit: 'caja',    sku: 'ACC-001', stock_alert_threshold: 2 },
    { id: 'ac2', name: 'Jeringas 5ml x 10',                    price:  3500, category: 'accesorios',   stock: 15, unit: 'caja',    sku: 'ACC-002', stock_alert_threshold: 4 },
    { id: 'ac3', name: 'Gasa Estéril 10x10 x 10',              price:  2200, category: 'accesorios',   stock: 22, unit: 'paquete', sku: 'ACC-003', stock_alert_threshold: 5 },
    { id: 'ac4', name: 'Aguja Hipodérmica 21G x 100',          price:  4500, category: 'accesorios',   stock: 10, unit: 'caja',    sku: 'ACC-004', stock_alert_threshold: 3 },
    { id: 'ac5', name: 'Vendaje Cohesivo 5cm x 4.5m',          price:  2800, category: 'accesorios',   stock: 20, unit: 'rollo',   sku: 'ACC-005', stock_alert_threshold: 5 },
    { id: 'ac6', name: 'Sonda Urinaria n°8 Fr',                price:  3500, category: 'accesorios',   stock: 8,  unit: 'unid.',   sku: 'ACC-006', stock_alert_threshold: 2 },
];
const DEFAULT_TERM_CONFIG: TerminalConfig = {
    tuu:       { api_key: '', terminal_id: '' },
    mp:        { access_token: '', terminal_id: '' },
    transbank: { commerce_code: '597020000540' },
};

/* ─── STORAGE ─── */
const SK = { products: 'vet_pos_products', sales: 'vet_pos_sales', caja: 'vet_pos_caja', term: 'vet_pos_term_config' };
const ls = {
    get: <T,>(k: string, def: T): T => { try { const r = localStorage.getItem(k); return r ? JSON.parse(r) : def; } catch { return def; } },
    set: (k: string, v: any) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch {} },
};

/* ─── HELPERS ─── */
const fp    = (n: number) => `$${n.toLocaleString('es-CL')}`;
const fdate = (d: string) => { try { return new Date(d).toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' }); } catch { return d; } };
const genBol = () => `BOL-${Date.now().toString(36).toUpperCase().slice(-6)}`;
const catDef  = (id: Category) => CATEGORIES.find(c => c.id === id) ?? CATEGORIES[0];
const payDef  = (id: PayMethod) => PAY_METHODS.find(p => p.id === id);
const payLbl  = (m: PayMethod) => payDef(m)?.label ?? m;
const payClr  = (m: PayMethod) => ({ efectivo: 'text-emerald-600', debito: 'text-blue-700', credito: 'text-violet-600', transferencia: 'text-orange-600' }[m] ?? 'text-slate-500');

/* ══════════════════════════════════════════════════════════════════
   BOLETA MODAL
══════════════════════════════════════════════════════════════════ */
const BoleteModal: React.FC<{ sale: Sale; onClose: () => void }> = ({ sale, onClose }) => (
    <div className="fixed inset-0 z-[400] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
        <div className="bg-white rounded-[2rem] w-full max-w-sm shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="bg-[#1E3A8A] px-6 pt-6 pb-5 text-white">
                <div className="flex items-center justify-between mb-3">
                    <div>
                        <p className="text-[9px] font-black text-blue-200 uppercase tracking-widest">{sale.docType} · {sale.saleNumber}</p>
                        {sale.clientName && <p className="text-sm font-bold text-blue-100">{sale.clientName}{sale.clientRut ? ` · ${sale.clientRut}` : ''}</p>}
                    </div>
                    <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all"><X size={14} /></button>
                </div>
                <p className="text-3xl font-black">{fp(sale.total)}</p>
                <p className="text-blue-200 text-xs mt-1">{sale.date ? fdate(sale.date) : 'Hoy'} · {sale.time} · {payLbl(sale.payment)}</p>
                {sale.payment === 'efectivo' && sale.cashReceived !== undefined && (
                    <p className="text-blue-200 text-[10px] mt-0.5">Recibido: {fp(sale.cashReceived)} · Vuelto: {fp(sale.changeGiven ?? 0)}</p>
                )}
            </div>
            <div className="px-6 py-4 max-h-72 overflow-y-auto space-y-2">
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest pb-1">Detalle</p>
                {sale.items.map((item, i) => (
                    <div key={i} className="flex items-center justify-between text-xs py-0.5">
                        <span className="text-slate-700 font-medium flex-1 truncate pr-2">{item.name}</span>
                        <span className="text-slate-400 mr-2 shrink-0">×{item.qty}</span>
                        <span className="font-black text-slate-800 shrink-0">{fp(item.price * item.qty)}</span>
                    </div>
                ))}
                <div className="pt-3 border-t border-slate-100 space-y-1">
                    <div className="flex justify-between text-xs text-slate-500"><span>Subtotal</span><span>{fp(sale.subtotal ?? sale.total)}</span></div>
                    {(sale.discountPct ?? 0) > 0 && <div className="flex justify-between text-xs text-emerald-600"><span>Dto. {sale.discountPct}%</span><span>-{fp(sale.discountAmt ?? 0)}</span></div>}
                    <div className="flex justify-between text-sm font-black text-slate-800 pt-1"><span>TOTAL</span><span>{fp(sale.total)}</span></div>
                </div>
                {sale.terminalProvider && (
                    <p className="text-[9px] text-slate-400 uppercase font-black tracking-widest pt-2">
                        Terminal: {sale.terminalProvider === 'mp' ? 'MercadoPago Point' : sale.terminalProvider === 'tuu' ? 'Tuu (Haulmer)' : 'Transbank Smart POS'}
                    </p>
                )}
            </div>
            <div className="px-6 pb-6 grid grid-cols-2 gap-3">
                <button className="flex items-center justify-center gap-1.5 py-2.5 bg-slate-100 text-slate-600 rounded-2xl text-[10px] font-black uppercase tracking-wide hover:bg-slate-200 transition-all"><Printer size={12} /> Imprimir</button>
                <button className="flex items-center justify-center gap-1.5 py-2.5 bg-slate-100 text-slate-600 rounded-2xl text-[10px] font-black uppercase tracking-wide hover:bg-slate-200 transition-all"><Share2 size={12} /> Compartir</button>
            </div>
        </div>
    </div>
);

/* ══════════════════════════════════════════════════════════════════
   CASH MODAL — Calculadora de vuelto (portado de dulcce-creacion)
══════════════════════════════════════════════════════════════════ */
const CashModal: React.FC<{ total: number; onConfirm: (received: number) => void; onClose: () => void }> = ({ total, onConfirm, onClose }) => {
    const [received, setReceived] = useState('');
    const num  = parseFloat(received || '0');
    const change = num - total;
    const enough = num >= total;

    const quickAmounts = useMemo(() => {
        const opts = new Set([total, Math.ceil(total / 1000) * 1000, Math.ceil(total / 5000) * 5000, Math.ceil(total / 10000) * 10000]);
        return [...opts].slice(0, 4);
    }, [total]);

    return (
        <div className="fixed inset-0 z-[400] bg-black/60 flex items-end sm:items-center justify-center" onClick={onClose}>
            <div className="bg-white rounded-t-[2rem] sm:rounded-[2rem] w-full max-w-sm shadow-2xl" onClick={e => e.stopPropagation()}>
                <div className="px-6 pt-6 pb-2">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Pago en efectivo</p>
                            <p className="text-2xl font-black text-slate-800">Total: {fp(total)}</p>
                        </div>
                        <button onClick={onClose} className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500"><X size={14} /></button>
                    </div>
                    <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1 block">Monto recibido</label>
                    <div className="relative">
                        <Banknote size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                            type="number"
                            value={received}
                            onChange={e => setReceived(e.target.value)}
                            placeholder={String(total)}
                            autoFocus
                            className="w-full pl-10 pr-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-2xl text-xl font-black text-slate-800 focus:outline-none focus:border-emerald-400 transition-all"
                        />
                    </div>
                    <div className="flex gap-2 mt-3 flex-wrap">
                        {quickAmounts.map(a => (
                            <button key={a} onClick={() => setReceived(String(a))}
                                className={`px-3 py-1.5 rounded-xl text-[10px] font-black transition-all ${num === a ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                                {fp(a)}
                            </button>
                        ))}
                    </div>
                </div>
                <div className={`mx-6 my-4 rounded-2xl p-4 flex items-center justify-between ${enough ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
                    <div>
                        <p className={`text-[9px] font-black uppercase tracking-widest ${enough ? 'text-emerald-600' : 'text-red-500'}`}>
                            {enough ? 'Vuelto' : 'Monto insuficiente'}
                        </p>
                        <p className={`text-2xl font-black ${enough ? 'text-emerald-600' : 'text-red-500'}`}>
                            {enough ? fp(change) : fp(total - num)}
                        </p>
                    </div>
                    {enough ? <CheckCircle size={28} className="text-emerald-400" /> : <AlertCircle size={28} className="text-red-400" />}
                </div>
                <div className="px-6 pb-6 grid grid-cols-2 gap-3">
                    <button onClick={onClose} className="py-3 bg-slate-100 text-slate-600 rounded-2xl text-[10px] font-black uppercase tracking-wide hover:bg-slate-200 transition-all">Cancelar</button>
                    <button onClick={() => enough && onConfirm(num)} disabled={!enough}
                        className="py-3 bg-emerald-500 text-white rounded-2xl text-[10px] font-black uppercase tracking-wide disabled:opacity-40 hover:bg-emerald-600 transition-all flex items-center justify-center gap-1.5">
                        <Check size={12} /> Confirmar
                    </button>
                </div>
            </div>
        </div>
    );
};

/* ══════════════════════════════════════════════════════════════════
   TERMINAL MODAL — Selección de maquinita de pago (dulcce-creacion)
══════════════════════════════════════════════════════════════════ */
const TerminalModal: React.FC<{
    total: number;
    state: TermState;
    provider: TermProvider | null;
    config: TerminalConfig;
    error: string;
    onSelect: (p: TermProvider) => void;
    onManualConfirm: () => void;
    onClose: () => void;
}> = ({ total, state, provider, config, error, onSelect, onManualConfirm, onClose }) => {
    const providers: { id: TermProvider; name: string; sub: string; configured: boolean; icon: React.ElementType; color: string }[] = [
        { id: 'tuu', name: 'Tuu (Haulmer)', sub: 'API real · Respuesta inmediata', configured: !!(config.tuu.api_key && config.tuu.terminal_id), icon: Terminal, color: 'bg-violet-600' },
        { id: 'mp',  name: 'MercadoPago Point', sub: 'API real · Respuesta inmediata', configured: !!(config.mp.access_token && config.mp.terminal_id), icon: Smartphone, color: 'bg-sky-500' },
        { id: 'transbank', name: 'Transbank Smart POS', sub: 'Confirmación manual', configured: true, icon: CreditCard, color: 'bg-[#1E3A8A]' },
    ];

    return (
        <div className="fixed inset-0 z-[400] bg-black/60 flex items-end sm:items-center justify-center" onClick={state === 'idle' ? onClose : undefined}>
            <div className="bg-white rounded-t-[2rem] sm:rounded-[2rem] w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}>
                {/* IDLE: select provider */}
                {state === 'idle' && (<>
                    <div className="px-6 pt-6 pb-2 flex items-center justify-between">
                        <div>
                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Pago con terminal</p>
                            <p className="text-xl font-black text-slate-800">{fp(total)}</p>
                        </div>
                        <button onClick={onClose} className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500"><X size={14} /></button>
                    </div>
                    <div className="px-6 py-4 space-y-3">
                        {providers.map(p => (
                            <button key={p.id} onClick={() => p.configured && onSelect(p.id)}
                                className={`w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition-all text-left ${p.configured ? 'border-slate-100 hover:border-[var(--vet-primary)]/40 hover:shadow-sm' : 'border-slate-100 opacity-50 cursor-not-allowed'}`}>
                                <div className={`w-12 h-12 ${p.color} rounded-2xl flex items-center justify-center shrink-0`}>
                                    <p.icon size={20} className="text-white" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-black text-slate-800">{p.name}</p>
                                    <p className="text-[10px] text-slate-400 font-medium">{p.sub}</p>
                                </div>
                                {p.configured
                                    ? <Wifi size={14} className="text-emerald-400 shrink-0" />
                                    : <Lock size={14} className="text-slate-300 shrink-0" />}
                            </button>
                        ))}
                    </div>
                    <p className="text-center text-[9px] text-slate-400 font-bold uppercase pb-5">Configure las credenciales en Ajustes &rarr; Terminal</p>
                </>)}

                {/* REQUESTING / WAITING */}
                {(state === 'requesting' || state === 'waiting') && (
                    <div className="px-6 py-10 text-center space-y-4">
                        <div className="w-20 h-20 rounded-full bg-[var(--vet-primary)]/10 flex items-center justify-center mx-auto">
                            {state === 'requesting'
                                ? <RefreshCw size={32} className="text-[var(--vet-primary)] animate-spin" />
                                : <Clock size={32} className="text-[var(--vet-primary)]" />}
                        </div>
                        <div>
                            <p className="font-black text-slate-800 text-lg">
                                {state === 'requesting' ? 'Enviando solicitud...' : 'Esperando confirmación'}
                            </p>
                            <p className="text-slate-400 text-sm mt-1">
                                {provider === 'transbank' ? 'Pase la tarjeta en el terminal Transbank' : 'El cliente debe aprobar en la máquina'}
                            </p>
                            <p className="font-black text-2xl text-[var(--vet-primary)] mt-3">{fp(total)}</p>
                        </div>
                        {provider === 'transbank' && state === 'waiting' && (
                            <button onClick={onManualConfirm}
                                className="w-full py-3 bg-emerald-500 text-white rounded-2xl text-[10px] font-black uppercase tracking-wide hover:bg-emerald-600 transition-all flex items-center justify-center gap-2">
                                <CheckCircle size={14} /> Cliente aprobó — Confirmar pago
                            </button>
                        )}
                        <button onClick={onClose} className="w-full py-2.5 bg-slate-100 text-slate-600 rounded-2xl text-[10px] font-black uppercase tracking-wide hover:bg-slate-200 transition-all">Cancelar</button>
                    </div>
                )}

                {/* CONFIRMED */}
                {state === 'confirmed' && (
                    <div className="px-6 py-10 text-center space-y-4">
                        <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto"><CheckCircle size={40} className="text-emerald-500" /></div>
                        <div>
                            <p className="font-black text-slate-800 text-lg">¡Pago aprobado!</p>
                            <p className="text-slate-400 text-sm mt-1">{fp(total)} · {providers.find(p => p.id === provider)?.name}</p>
                        </div>
                    </div>
                )}

                {/* ERROR */}
                {state === 'error' && (
                    <div className="px-6 py-10 text-center space-y-4">
                        <div className="w-20 h-20 rounded-full bg-red-50 flex items-center justify-center mx-auto"><AlertCircle size={40} className="text-red-400" /></div>
                        <div>
                            <p className="font-black text-slate-800 text-lg">Error en terminal</p>
                            <p className="text-slate-400 text-sm mt-1">{error}</p>
                        </div>
                        <button onClick={onClose} className="w-full py-3 bg-slate-100 text-slate-600 rounded-2xl text-[10px] font-black uppercase tracking-wide">Cerrar</button>
                    </div>
                )}
            </div>
        </div>
    );
};

/* ══════════════════════════════════════════════════════════════════
   PRODUCT FORM MODAL
══════════════════════════════════════════════════════════════════ */
interface ProdForm { name: string; price: string; category: Category; stock: string; stock_alert_threshold: string; unit: string; sku: string; }
const ProductFormModal: React.FC<{ product: Product | null; onSave: (d: Omit<Product, 'id'>) => void; onClose: () => void }> = ({ product, onSave, onClose }) => {
    const [f, setF] = useState<ProdForm>({
        name: product?.name ?? '',
        price: product ? String(product.price) : '',
        category: product?.category ?? 'servicios',
        stock: product ? String(product.stock) : '0',
        stock_alert_threshold: product?.stock_alert_threshold !== undefined ? String(product.stock_alert_threshold) : '5',
        unit: product?.unit ?? 'servicio',
        sku: product?.sku ?? ''
    });
    const upd = (k: keyof ProdForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setF(p => ({ ...p, [k]: e.target.value }));
    const inp = "w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-800 focus:outline-none focus:border-[var(--vet-primary)]/60 transition-all";
    const lbl = "text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1 block";
    return (
        <div className="fixed inset-0 z-[400] bg-black/60 flex items-end sm:items-center justify-center" onClick={onClose}>
            <div className="bg-white rounded-t-[2rem] sm:rounded-[2rem] w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}>
                <div className="px-6 pt-6 pb-2 flex items-center justify-between">
                    <h3 className="text-base font-black text-slate-800">{product ? 'Editar Producto' : 'Nuevo Producto'}</h3>
                    <button onClick={onClose} className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500"><X size={14} /></button>
                </div>
                <div className="px-6 py-4 space-y-3">
                    <div><label className={lbl}>Nombre *</label><input value={f.name} onChange={upd('name')} placeholder="ej. Amoxicilina 250mg" className={inp} /></div>
                    <div className="grid grid-cols-2 gap-3">
                        <div><label className={lbl}>Precio * (CLP)</label><input type="number" value={f.price} onChange={upd('price')} placeholder="15000" className={inp} /></div>
                        <div>
                            <label className={lbl}>Categoría</label>
                            <select value={f.category} onChange={upd('category')} className={inp}>
                                {CATEGORIES.filter(c => c.id !== 'todo').map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                            </select>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div><label className={lbl}>Unidad</label><input value={f.unit} onChange={upd('unit')} placeholder="unid., caja, dosis..." className={inp} /></div>
                        <div><label className={lbl}>SKU (opcional)</label><input value={f.sku} onChange={upd('sku')} placeholder="MED-001" className={inp} /></div>
                    </div>
                    {/* Stock management */}
                    <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4 space-y-3">
                        <p className="text-[9px] font-black text-amber-700 uppercase tracking-widest">Control de Stock</p>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className={lbl}>Stock actual (unidades)</label>
                                <input type="number" value={f.stock} onChange={upd('stock')} placeholder="0" min="0" className={inp} />
                            </div>
                            <div>
                                <label className={lbl}>Alerta bajo stock (≤)</label>
                                <input type="number" value={f.stock_alert_threshold} onChange={upd('stock_alert_threshold')} placeholder="5" min="0" className={inp} />
                                <p className="text-[8px] text-amber-600 font-bold mt-1">Notificación push al llegar a este nivel</p>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="px-6 pb-6 grid grid-cols-2 gap-3">
                    <button onClick={onClose} className="py-3 bg-slate-100 text-slate-600 rounded-2xl text-[10px] font-black uppercase tracking-wide hover:bg-slate-200 transition-all">Cancelar</button>
                    <button onClick={() => {
                        if (!f.name.trim() || !f.price) return;
                        onSave({ name: f.name.trim(), price: parseFloat(f.price) || 0, category: f.category, stock: parseInt(f.stock) || 0, stock_alert_threshold: parseInt(f.stock_alert_threshold) || 5, unit: f.unit.trim() || 'unid.', sku: f.sku.trim() });
                    }} disabled={!f.name.trim() || !f.price}
                        className="py-3 bg-[var(--vet-primary)] text-white rounded-2xl text-[10px] font-black uppercase tracking-wide disabled:opacity-40 flex items-center justify-center gap-1.5 hover:opacity-90 transition-all">
                        <Save size={12} /> {product ? 'Actualizar' : 'Crear'}
                    </button>
                </div>
            </div>
        </div>
    );
};

/* ══════════════════════════════════════════════════════════════════
   CIERRE DE CAJA VIEW (portado de dulcce-creacion cash_reports)
══════════════════════════════════════════════════════════════════ */
const CajaView: React.FC<{
    sales: Sale[];
    session: CajaSession | null;
    onOpenCaja: (startAmount: number) => void;
    onCloseCaja: (endAmount: number, notes: string) => void;
}> = ({ sales, session, onOpenCaja, onCloseCaja }) => {
    const [startInput, setStartInput] = useState('');
    const [endInput,   setEndInput]   = useState('');
    const [notes,      setNotes]      = useState('');

    const today = new Date().toDateString();
    const todaySales = sales.filter(s => { try { return new Date(s.date).toDateString() === today && s.status === 'completed'; } catch { return false; } });
    const totalSales  = todaySales.reduce((s, v) => s + v.total, 0);
    const totalEfect  = todaySales.filter(s => s.payment === 'efectivo').reduce((s, v) => s + v.total, 0);
    const totalTarj   = todaySales.filter(s => ['debito', 'credito'].includes(s.payment)).reduce((s, v) => s + v.total, 0);
    const totalTrans  = todaySales.filter(s => s.payment === 'transferencia').reduce((s, v) => s + v.total, 0);
    const totalTerm   = todaySales.filter(s => s.terminalProvider).reduce((s, v) => s + v.total, 0);
    const nBoletas    = todaySales.filter(s => s.docType === 'BOLETA').length;
    const nFacturas   = todaySales.filter(s => s.docType === 'FACTURA').length;

    const startAmt    = session ? session.startAmount : parseFloat(startInput || '0');
    const endAmt      = parseFloat(endInput || '0');
    const cajaEfect   = startAmt + totalEfect;
    const diferencia  = endAmt - cajaEfect;

    const StatCard: React.FC<{ label: string; value: string; icon: React.ElementType; color: string }> = ({ label, value, icon: Icon, color }) => (
        <div className="bg-white border border-slate-100 rounded-2xl p-4">
            <div className={`w-8 h-8 rounded-xl ${color} flex items-center justify-center mb-2`}><Icon size={14} className="text-white" /></div>
            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{label}</p>
            <p className="text-lg font-black text-slate-800">{value}</p>
        </div>
    );

    return (
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
            <div>
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Resumen del día</p>
                <h3 className="text-base font-black text-slate-800">{new Date().toLocaleDateString('es-CL', { weekday: 'long', day: '2-digit', month: 'long' })}</h3>
            </div>

            {/* Summary cards */}
            <div className="grid grid-cols-2 gap-3">
                <StatCard label="Ventas hoy" value={fp(totalSales)} icon={DollarSign} color="bg-emerald-500" />
                <StatCard label="Transacciones" value={`${todaySales.length}`} icon={ReceiptText} color="bg-[var(--vet-primary)]" />
                <StatCard label="Efectivo" value={fp(totalEfect)} icon={Banknote} color="bg-amber-500" />
                <StatCard label="Tarjetas" value={fp(totalTarj + totalTerm)} icon={CreditCard} color="bg-violet-600" />
            </div>

            <div className="grid grid-cols-3 gap-2 text-center">
                {[['Transferencia', fp(totalTrans)], [`${nBoletas} Boleta${nBoletas !== 1 ? 's' : ''}`, ''], [`${nFacturas} Factura${nFacturas !== 1 ? 's' : ''}`, '']].map(([lbl, val]) => (
                    <div key={lbl} className="bg-slate-50 rounded-2xl p-3">
                        <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{lbl}</p>
                        {val && <p className="text-sm font-black text-slate-700">{val}</p>}
                    </div>
                ))}
            </div>

            {/* Apertura de caja */}
            {!session && (
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 space-y-3">
                    <p className="text-sm font-black text-amber-800">Apertura de caja</p>
                    <div>
                        <label className="text-[9px] font-black text-amber-600 uppercase tracking-widest mb-1 block">Fondo inicial ($)</label>
                        <input type="number" value={startInput} onChange={e => setStartInput(e.target.value)} placeholder="0"
                            className="w-full px-3 py-2.5 bg-white border border-amber-200 rounded-xl text-sm text-slate-800 focus:outline-none focus:border-amber-400 transition-all" />
                    </div>
                    <button onClick={() => onOpenCaja(parseFloat(startInput || '0'))}
                        className="w-full py-3 bg-amber-500 text-white rounded-2xl text-[10px] font-black uppercase tracking-wide hover:bg-amber-600 transition-all">
                        Abrir caja
                    </button>
                </div>
            )}

            {/* Cierre de caja */}
            {session && session.status === 'open' && (
                <div className="bg-white border border-slate-100 rounded-2xl p-4 space-y-3">
                    <div className="flex items-center justify-between">
                        <p className="text-sm font-black text-slate-800">Cierre de caja</p>
                        <span className="text-[9px] font-black text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full uppercase">Abierta</span>
                    </div>
                    <div className="bg-slate-50 rounded-xl p-3 space-y-1.5 text-xs">
                        <div className="flex justify-between"><span className="text-slate-500">Fondo apertura</span><span className="font-bold">{fp(session.startAmount)}</span></div>
                        <div className="flex justify-between"><span className="text-slate-500">+ Efectivo ventas</span><span className="font-bold text-emerald-600">+{fp(totalEfect)}</span></div>
                        <div className="flex justify-between font-black text-slate-800 border-t border-slate-200 pt-1.5"><span>= En caja (esperado)</span><span>{fp(cajaEfect)}</span></div>
                    </div>
                    <div>
                        <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1 block">Recuento físico de efectivo ($)</label>
                        <input type="number" value={endInput} onChange={e => setEndInput(e.target.value)} placeholder="0"
                            className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-800 focus:outline-none focus:border-[var(--vet-primary)]/50 transition-all" />
                    </div>
                    {endInput && (
                        <div className={`rounded-xl p-3 flex items-center justify-between ${diferencia >= 0 ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
                            <p className={`text-xs font-black ${diferencia >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                                {diferencia >= 0 ? `Sobrante: ${fp(diferencia)}` : `Faltante: ${fp(Math.abs(diferencia))}`}
                            </p>
                            {diferencia >= 0 ? <ArrowUpRight size={16} className="text-emerald-500" /> : <ArrowDownRight size={16} className="text-red-400" />}
                        </div>
                    )}
                    <div>
                        <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1 block">Observaciones</label>
                        <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder="Notas del cierre..."
                            className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none resize-none" />
                    </div>
                    <button onClick={() => onCloseCaja(endAmt, notes)} disabled={!endInput}
                        className="w-full py-3 bg-[var(--vet-primary)] text-white rounded-2xl text-[10px] font-black uppercase tracking-wide disabled:opacity-40 hover:opacity-90 transition-all flex items-center justify-center gap-2">
                        <Lock size={12} /> Cerrar caja del día
                    </button>
                </div>
            )}

            {session && session.status === 'closed' && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 text-center">
                    <CheckCircle size={24} className="text-emerald-500 mx-auto mb-2" />
                    <p className="text-sm font-black text-emerald-800">Caja cerrada</p>
                    <p className="text-xs text-emerald-600">Cerrada a las {new Date(session.closedAt ?? '').toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })}</p>
                </div>
            )}
        </div>
    );
};

/* ══════════════════════════════════════════════════════════════════
   REPORTES VIEW (portado de contabilidad-propyme dulcce-creacion)
══════════════════════════════════════════════════════════════════ */
const ReportesView: React.FC<{
    sales: Sale[];
    termConfig: TerminalConfig;
    onSaveTermConfig: (c: TerminalConfig) => void;
}> = ({ sales, termConfig, onSaveTermConfig }) => {
    const [period, setPeriod] = useState<'hoy' | 'semana' | 'mes'>('hoy');
    const [showTermSettings, setShowTermSettings] = useState(false);
    const [tc, setTc] = useState<TerminalConfig>(termConfig);

    const filtered = useMemo(() => {
        const now = new Date();
        return sales.filter(s => {
            if (s.status !== 'completed') return false;
            try {
                const d = new Date(s.date);
                if (period === 'hoy') return d.toDateString() === now.toDateString();
                if (period === 'semana') { const wAgo = new Date(now); wAgo.setDate(wAgo.getDate() - 7); return d >= wAgo; }
                const mAgo = new Date(now.getFullYear(), now.getMonth(), 1);
                return d >= mAgo;
            } catch { return false; }
        });
    }, [sales, period]);

    const revenue   = filtered.reduce((s, v) => s + v.total, 0);
    const byMethod  = { efectivo: 0, debito: 0, credito: 0, transferencia: 0 } as Record<PayMethod, number>;
    const byDocType = { BOLETA: 0, FACTURA: 0 } as Record<DocType, number>;
    const byCat: Record<string, number> = {};
    filtered.forEach(s => {
        byMethod[s.payment] = (byMethod[s.payment] ?? 0) + s.total;
        byDocType[s.docType] = (byDocType[s.docType] ?? 0) + 1;
        s.items.forEach(it => { byCat[it.category] = (byCat[it.category] ?? 0) + it.price * it.qty; });
    });
    const topCats = Object.entries(byCat).sort(([,a],[,b]) => b - a).slice(0, 5);

    const Bar: React.FC<{ label: string; value: number; max: number; color: string }> = ({ label, value, max, color }) => (
        <div className="space-y-1">
            <div className="flex justify-between text-xs"><span className="font-medium text-slate-600">{label}</span><span className="font-black text-slate-800">{fp(value)}</span></div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden"><div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${max > 0 ? (value / max) * 100 : 0}%` }} /></div>
        </div>
    );

    const upd = (section: keyof TerminalConfig, field: string) =>
        (e: React.ChangeEvent<HTMLInputElement>) =>
            setTc(prev => ({ ...prev, [section]: { ...prev[section], [field]: e.target.value } }));

    return (
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
            {/* Period tabs */}
            <div>
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-3">Período</p>
                <div className="flex gap-2">
                    {(['hoy', 'semana', 'mes'] as const).map(p => (
                        <button key={p} onClick={() => setPeriod(p)}
                            className={`flex-1 py-2 rounded-xl text-[10px] font-black uppercase tracking-wide transition-all ${period === p ? 'bg-[var(--vet-primary)] text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>
                            {p === 'hoy' ? 'Hoy' : p === 'semana' ? 'Semana' : 'Mes'}
                        </button>
                    ))}
                </div>
            </div>

            {/* Revenue hero */}
            <div className="bg-[#1E3A8A] rounded-2xl p-5 text-white">
                <p className="text-[9px] font-black text-blue-200 uppercase tracking-widest">Ingresos totales</p>
                <p className="text-3xl font-black mt-1">{fp(revenue)}</p>
                <div className="flex items-center gap-4 mt-3 text-xs text-blue-200">
                    <span>{filtered.length} venta{filtered.length !== 1 ? 's' : ''}</span>
                    <span>·</span>
                    <span>{byDocType.BOLETA} boleta{byDocType.BOLETA !== 1 ? 's' : ''}</span>
                    <span>·</span>
                    <span>{byDocType.FACTURA} factura{byDocType.FACTURA !== 1 ? 's' : ''}</span>
                </div>
            </div>

            {/* By payment method */}
            <div className="bg-white border border-slate-100 rounded-2xl p-4 space-y-3">
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Por método de pago</p>
                {PAY_METHODS.map(m => (
                    <Bar key={m.id} label={m.label} value={byMethod[m.id] ?? 0} max={revenue} color={m.color.replace('bg-', 'bg-')} />
                ))}
            </div>

            {/* Top categories */}
            {topCats.length > 0 && (
                <div className="bg-white border border-slate-100 rounded-2xl p-4 space-y-3">
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Por categoría</p>
                    {topCats.map(([cat, val]) => {
                        const def = catDef(cat as Category);
                        return <Bar key={cat} label={def.label} value={val} max={topCats[0][1]} color="bg-[var(--vet-primary)]" />;
                    })}
                </div>
            )}

            {/* Terminal config */}
            <div className="bg-white border border-slate-100 rounded-2xl overflow-hidden">
                <button onClick={() => setShowTermSettings(s => !s)}
                    className="w-full flex items-center justify-between px-4 py-4 text-left">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-slate-100 rounded-xl flex items-center justify-center"><Terminal size={14} className="text-slate-600" /></div>
                        <div>
                            <p className="text-sm font-black text-slate-800">Ajustes de Terminal</p>
                            <p className="text-[10px] text-slate-400">Tuu · MercadoPago · Transbank</p>
                        </div>
                    </div>
                    <ChevronRight size={14} className={`text-slate-400 transition-transform ${showTermSettings ? 'rotate-90' : ''}`} />
                </button>
                {showTermSettings && (
                    <div className="px-4 pb-4 space-y-4 border-t border-slate-100 pt-4">
                        {/* Tuu */}
                        <div className="space-y-2">
                            <p className="text-[9px] font-black text-violet-600 uppercase tracking-widest">Tuu (Haulmer)</p>
                            <input value={tc.tuu.api_key} onChange={upd('tuu', 'api_key')} placeholder="API Key" type="password"
                                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none" />
                            <input value={tc.tuu.terminal_id} onChange={upd('tuu', 'terminal_id')} placeholder="Número de serie del terminal"
                                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none" />
                        </div>
                        {/* MercadoPago */}
                        <div className="space-y-2">
                            <p className="text-[9px] font-black text-sky-600 uppercase tracking-widest">MercadoPago Point</p>
                            <input value={tc.mp.access_token} onChange={upd('mp', 'access_token')} placeholder="Access Token" type="password"
                                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none" />
                            <input value={tc.mp.terminal_id} onChange={upd('mp', 'terminal_id')} placeholder="Device ID (terminal físico)"
                                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none" />
                        </div>
                        {/* Transbank */}
                        <div className="space-y-2">
                            <p className="text-[9px] font-black text-blue-800 uppercase tracking-widest">Transbank Smart POS</p>
                            <input value={tc.transbank.commerce_code} onChange={upd('transbank', 'commerce_code')} placeholder="Código de comercio"
                                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none" />
                            <p className="text-[9px] text-slate-400">Transbank Smart POS requiere confirmación manual del cajero</p>
                        </div>
                        <button onClick={() => { onSaveTermConfig(tc); setShowTermSettings(false); }}
                            className="w-full py-2.5 bg-[var(--vet-primary)] text-white rounded-xl text-[10px] font-black uppercase tracking-wide hover:opacity-90 transition-all flex items-center justify-center gap-1.5">
                            <Save size={11} /> Guardar ajustes
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

/* ══════════════════════════════════════════════════════════════════
   MAIN COMPONENT — VetPOS
══════════════════════════════════════════════════════════════════ */
export const VetPOS: React.FC<{ vetId?: string | number }> = ({ vetId }) => {
    /* products */
    const [products, setProducts]      = useState<Product[]>([]);
    const [editingProd, setEditingProd]= useState<Product | null>(null);
    const [showProdForm, setShowProdForm] = useState(false);

    /* catalog filter */
    const [category, setCategory] = useState<Category>('todo');
    const [query, setQuery]       = useState('');
    const [manCat, setManCat]     = useState<Category>('todo');

    /* cart */
    const [cart, setCart]         = useState<CartItem[]>([]);
    const [client, setClient]     = useState('');
    const [clientRut, setClientRut] = useState('');
    const [discount, setDiscount] = useState(0);
    const [payment, setPayment]   = useState<PayMethod | null>(null);
    const [docType, setDocType]   = useState<DocType>('BOLETA');

    /* modals */
    const [showCashModal, setShowCashModal]     = useState(false);
    const [showTermModal, setShowTermModal]     = useState(false);
    const [termState, setTermState]             = useState<TermState>('idle');
    const [termProvider, setTermProvider]       = useState<TermProvider | null>(null);
    const [termError, setTermError]             = useState('');
    const [showTicket, setShowTicket]           = useState(false);
    const [selectedSale, setSelectedSale]       = useState<Sale | null>(null);
    const [showHistory, setShowHistory]         = useState(false);

    /* views */
    const [view, setView]             = useState<POSView>('pos');
    const [mobileTab, setMobileTab]   = useState<'catalog' | 'cart'>('catalog');

    /* sales & caja */
    const [sales, setSales]           = useState<Sale[]>([]);
    const [lastTicket, setLastTicket] = useState<Sale | null>(null);
    const [cajaSession, setCajaSession] = useState<CajaSession | null>(null);

    /* terminal config */
    const [termConfig, setTermConfig] = useState<TerminalConfig>(DEFAULT_TERM_CONFIG);

    const ticketRef = useRef<HTMLDivElement>(null);

    /* ── Load on mount: DB-first when vetId is available ── */
    useEffect(() => {
        if (!vetId) {
            setProducts(ls.get(SK.products, INITIAL_PRODUCTS));
            return;
        }
        api.getPOSProducts(vetId).then(async (res) => {
            if (res.success && Array.isArray(res.products) && res.products.length > 0) {
                const dbProds: Product[] = res.products.map((p: any) => ({
                    id: String(p.id),
                    name: p.name,
                    price: p.price,
                    category: p.category as Category,
                    stock: p.stock_quantity,
                    unit: p.unit || 'unid.',
                    sku: p.sku || '',
                    stock_alert_threshold: p.stock_alert_threshold ?? 5,
                }));
                setProducts(dbProds);
                ls.set(SK.products, dbProds);
            } else {
                // Primer uso de esta veterinaria — subir catálogo local a la BD
                const local: Product[] = ls.get(SK.products, INITIAL_PRODUCTS);
                const results = await Promise.all(
                    local.map(p =>
                        api.addPOSProduct(vetId, {
                            name: p.name, category: p.category, price: p.price,
                            unit: p.unit, sku: p.sku, stock_quantity: p.stock,
                            stock_alert_threshold: p.stock_alert_threshold ?? 5,
                        }).catch(() => null)
                    )
                );
                const seeded: Product[] = local.map((p, i) => {
                    const r = results[i];
                    return r?.success ? { ...p, id: String(r.id) } : p;
                });
                setProducts(seeded);
                ls.set(SK.products, seeded);
            }
        }).catch(() => {
            setProducts(ls.get(SK.products, INITIAL_PRODUCTS));
        });
    }, [vetId]);
    useEffect(() => { setSales(ls.get(SK.sales, [])); }, []);
    useEffect(() => { setCajaSession(ls.get(SK.caja, null)); }, []);
    useEffect(() => { setTermConfig(ls.get(SK.term, DEFAULT_TERM_CONFIG)); }, []);

    // Cargar consumos pendientes de hospitalización
    useEffect(() => {
        try {
            const pending = localStorage.getItem('vet_pos_pending_hosp_cart');
            if (pending) {
                const items = JSON.parse(pending);
                if (Array.isArray(items) && items.length > 0) {
                    setCart(prev => {
                        const newCart = [...prev];
                        items.forEach(it => {
                            const ex = newCart.find(i => i.id === it.id || i.sku === it.sku);
                            if (ex) ex.qty += it.qty;
                            else newCart.push(it);
                        });
                        return newCart;
                    });
                    
                    // Si viene un nombre de cliente asignado
                    const clientName = localStorage.getItem('vet_pos_pending_hosp_client');
                    if (clientName) {
                        setClient(clientName);
                        localStorage.removeItem('vet_pos_pending_hosp_client');
                    }
                    
                    localStorage.removeItem('vet_pos_pending_hosp_cart');
                }
            }
        } catch (e) {
            console.error("Error loading pending hosp cart:", e);
        }
    }, [view]);

    /* ── Filtered products ── */
    const filtered = useMemo(() => {
        let list = category === 'todo' ? products : products.filter(p => p.category === category);
        if (query.trim()) { const q = query.toLowerCase(); list = list.filter(p => p.name.toLowerCase().includes(q) || p.sku.toLowerCase().includes(q)); }
        return list;
    }, [products, category, query]);
    const manageList = manCat === 'todo' ? products : products.filter(p => p.category === manCat);

    /* ── Cart ops ── */
    const addToCart  = (p: Product) => setCart(prev => { const ex = prev.find(i => i.id === p.id); if (ex) return prev.map(i => i.id === p.id ? { ...i, qty: i.qty + 1 } : i); return [...prev, { ...p, qty: 1 }]; });
    const updateQty  = (id: string, d: number) => setCart(prev => prev.map(i => i.id === id ? { ...i, qty: Math.max(0, i.qty + d) } : i).filter(i => i.qty > 0));
    const removeItem = (id: string) => setCart(prev => prev.filter(i => i.id !== id));
    const clearCart  = useCallback(() => { setCart([]); setClient(''); setClientRut(''); setDiscount(0); setPayment(null); setDocType('BOLETA'); }, []);

    const subtotal    = cart.reduce((s, i) => s + i.price * i.qty, 0);
    const discountAmt = Math.round(subtotal * discount / 100);
    const total       = subtotal - discountAmt;
    const cartCount   = cart.reduce((s, i) => s + i.qty, 0);

    /* ── Persist sale ── */
    const persistSale = useCallback((sale: Sale) => {
        setSales(prev => { const next = [sale, ...prev]; ls.set(SK.sales, next.slice(0, 500)); return next; });
        api.createVetPOSSale({ ...sale, payment_method: sale.payment, document_type: sale.docType, client_name: sale.clientName, client_rut: sale.clientRut, terminal_provider: sale.terminalProvider, terminal_response: sale.terminalResponse }).catch(() => {});
    }, []);

    /* ── Build & complete sale ── */
    const buildSale = useCallback((extra: Partial<Sale> = {}): Sale => ({
        id: `s${Date.now()}`,
        saleNumber: genBol(),
        items: [...cart],
        subtotal, discountPct: discount, discountAmt, total,
        payment: payment!,
        docType,
        clientName: client || undefined,
        clientRut: docType === 'FACTURA' ? clientRut || undefined : undefined,
        date: new Date().toISOString(),
        time: new Date().toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' }),
        status: 'completed',
        ...extra,
    }), [cart, subtotal, discount, discountAmt, total, payment, docType, client, clientRut]);

    const completeSale = useCallback((extra: Partial<Sale> = {}) => {
        const sale = buildSale(extra);
        persistSale(sale);
        setLastTicket(sale);
        clearCart();
        setShowTicket(true);
        setShowTermModal(false);
        setShowCashModal(false);
    }, [buildSale, persistSale, clearCart]);

    /* ── Payment dispatch ── */
    const handleCompleteSale = useCallback(() => {
        if (!payment || cart.length === 0) return;
        if (payment === 'efectivo') { setShowCashModal(true); return; }
        if (payment === 'debito' || payment === 'credito') { setShowTermModal(true); return; }
        completeSale();
    }, [payment, cart.length, completeSale]);

    /* ── Terminal flow ── */
    const handleSelectTerminal = useCallback(async (provider: TermProvider) => {
        setTermProvider(provider);
        setTermState('requesting');
        setTermError('');
        try {
            const res = await api.initVetTerminalPayment({
                provider,
                amount: total,
                reference: `VET-${Date.now()}`,
                config: termConfig[provider],
            });
            if (res.success) {
                setTermState('waiting');
                if (provider !== 'transbank') {
                    // Tuu/MP: auto-confirm after 30s or manual
                    setTimeout(() => {
                        setTermState('confirmed');
                        setTimeout(() => completeSale({ payment, terminalProvider: provider, terminalResponse: res.data }), 1500);
                    }, 3000);
                }
            } else {
                setTermState('error');
                setTermError(res.message ?? 'Error desconocido');
            }
        } catch {
            setTermState('error');
            setTermError('No se pudo conectar con el servidor');
        }
    }, [total, termConfig, completeSale, payment]);

    /* ── Product CRUD ── */
    const handleSaveProd = async (data: Omit<Product, 'id'>) => {
        if (!editingProd) {
            const p: Product = { id: `local-${Date.now()}`, ...data };
            setProducts(prev => { const n = [...prev, p]; ls.set(SK.products, n); return n; });
            if (vetId) {
                const res = await api.addPOSProduct(vetId, { name: data.name, category: data.category, price: data.price, unit: data.unit, sku: data.sku, stock_quantity: data.stock, stock_alert_threshold: data.stock_alert_threshold ?? 5 });
                if (res.success) {
                    setProducts(prev => { const n = prev.map(x => x.id === p.id ? { ...x, id: String(res.id) } : x); ls.set(SK.products, n); return n; });
                }
            }
        } else {
            setProducts(prev => { const n = prev.map(p => p.id === editingProd.id ? { ...p, ...data } : p); ls.set(SK.products, n); return n; });
            if (vetId && !editingProd.id.startsWith('local-')) {
                await api.updatePOSProduct({ id: parseInt(editingProd.id), name: data.name, category: data.category, price: data.price, unit: data.unit, sku: data.sku ?? '', stock_quantity: data.stock, stock_alert_threshold: data.stock_alert_threshold ?? 5 });
            }
        }
        setShowProdForm(false);
    };
    const handleDelProd = (p: Product) => {
        if (!window.confirm(`¿Eliminar "${p.name}"?`)) return;
        setProducts(prev => { const n = prev.filter(x => x.id !== p.id); ls.set(SK.products, n); return n; });
        const numId = parseInt(p.id);
        if (vetId && !isNaN(numId)) {
            api.deletePOSProduct(numId).catch(() => {});
        }
    };

    /* ── Caja ── */
    const handleOpenCaja = (startAmount: number) => {
        const session: CajaSession = { id: `caja-${Date.now()}`, date: new Date().toISOString().split('T')[0], openedAt: new Date().toISOString(), startAmount, status: 'open' };
        setCajaSession(session);
        ls.set(SK.caja, session);
        api.openVetCaja({ start_amount: startAmount }).catch(() => {});
    };
    const handleCloseCaja = (endAmount: number, notes: string) => {
        if (!cajaSession) return;
        const today = new Date().toDateString();
        const todaySales = sales.filter(s => { try { return new Date(s.date).toDateString() === today && s.status === 'completed'; } catch { return false; } });
        const totalSales = todaySales.reduce((s, v) => s + v.total, 0);
        const totalEfect = todaySales.filter(s => s.payment === 'efectivo').reduce((s, v) => s + v.total, 0);
        const totalTarj  = todaySales.filter(s => ['debito', 'credito'].includes(s.payment)).reduce((s, v) => s + v.total, 0);
        const totalTrans = todaySales.filter(s => s.payment === 'transferencia').reduce((s, v) => s + v.total, 0);
        const diff = endAmount - (cajaSession.startAmount + totalEfect);
        const closed: CajaSession = { ...cajaSession, status: 'closed', endAmount, closedAt: new Date().toISOString() };
        setCajaSession(closed);
        ls.set(SK.caja, closed);
        api.closeVetCaja(cajaSession.id, { end_amount: endAmount, total_sales: totalSales, total_efectivo: totalEfect, total_tarjeta: totalTarj, total_transferencia: totalTrans, n_sales: todaySales.length, n_boletas: todaySales.filter(s => s.docType === 'BOLETA').length, n_facturas: todaySales.filter(s => s.docType === 'FACTURA').length, difference: diff, notes }).catch(() => {});
    };

    /* ── Daily stats ── */
    const salesToday = useMemo(() => {
        const today = new Date().toDateString();
        return sales.filter(s => { try { return new Date(s.date).toDateString() === today && s.status === 'completed'; } catch { return false; } });
    }, [sales]);
    const totalDay = salesToday.reduce((s, v) => s + v.total, 0);

    /* ── NAV items ── */
    const NAV: { id: POSView; label: string; icon: React.ElementType }[] = [
        { id: 'pos',      label: 'Caja',      icon: ShoppingCart },
        { id: 'manage',   label: 'Productos', icon: Package },
        { id: 'caja',     label: 'Cierre',    icon: DollarSign },
        { id: 'reportes', label: 'Reportes',  icon: BarChart2 },
    ];

    return (
        <div className="h-full flex flex-col overflow-hidden bg-slate-50">

            {/* ── MODALS ── */}
            {selectedSale && <BoleteModal sale={selectedSale} onClose={() => setSelectedSale(null)} />}
            {showProdForm && <ProductFormModal product={editingProd} onSave={handleSaveProd} onClose={() => setShowProdForm(false)} />}
            {showCashModal && (
                <CashModal total={total} onClose={() => setShowCashModal(false)}
                    onConfirm={received => completeSale({ payment: 'efectivo', cashReceived: received, changeGiven: received - total })} />
            )}
            {showTermModal && (
                <TerminalModal total={total} state={termState} provider={termProvider} config={termConfig} error={termError}
                    onSelect={handleSelectTerminal}
                    onManualConfirm={() => completeSale({ payment, terminalProvider: termProvider ?? undefined })}
                    onClose={() => { setShowTermModal(false); setTermState('idle'); setTermProvider(null); }} />
            )}

            {/* POST-SALE TICKET */}
            {showTicket && lastTicket && (
                <div className="fixed inset-0 z-[300] bg-black/60 flex items-center justify-center p-4">
                    <div className="bg-white rounded-[2rem] w-full max-w-sm shadow-2xl overflow-hidden">
                        <div className="bg-emerald-500 px-6 pt-6 pb-8 text-white text-center">
                            <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3"><Check size={28} /></div>
                            <h3 className="text-lg font-black uppercase tracking-tight">Venta completada</h3>
                            <p className="text-2xl font-black mt-1">{fp(lastTicket.total)}</p>
                            <p className="text-emerald-100 text-xs mt-1">{lastTicket.saleNumber} · {lastTicket.time} · {payLbl(lastTicket.payment)}</p>
                            {lastTicket.payment === 'efectivo' && lastTicket.cashReceived !== undefined && (
                                <p className="text-emerald-100 text-[10px] mt-0.5">Recibido: {fp(lastTicket.cashReceived)} · Vuelto: {fp(lastTicket.changeGiven ?? 0)}</p>
                            )}
                        </div>
                        <div ref={ticketRef} className="px-6 py-4 space-y-2">
                            {lastTicket.clientName && <div className="flex items-center gap-2 pb-2 border-b border-slate-100"><User size={13} className="text-slate-400" /><span className="text-xs font-bold text-slate-600">{lastTicket.clientName}</span></div>}
                            {lastTicket.items.map((item, i) => (
                                <div key={i} className="flex items-center justify-between text-xs">
                                    <span className="text-slate-700 font-medium flex-1 truncate">{item.name}</span>
                                    <span className="text-slate-400 mx-2">×{item.qty}</span>
                                    <span className="font-black text-slate-800 shrink-0">{fp(item.price * item.qty)}</span>
                                </div>
                            ))}
                            {lastTicket.discountPct > 0 && <div className="flex justify-between text-xs text-emerald-600 border-t border-slate-100 pt-2"><span>Dto. {lastTicket.discountPct}%</span><span>-{fp(lastTicket.discountAmt)}</span></div>}
                            <div className="pt-2 border-t border-slate-200 flex justify-between font-black text-sm text-slate-800"><span>TOTAL</span><span>{fp(lastTicket.total)}</span></div>
                        </div>
                        <div className="px-6 pb-6 grid grid-cols-2 gap-3">
                            <button onClick={() => setSelectedSale(lastTicket)} className="flex items-center justify-center gap-1.5 py-3 bg-slate-100 text-slate-600 rounded-2xl text-[10px] font-black uppercase tracking-wide hover:bg-slate-200 transition-all"><FileText size={13} /> Ver boleta</button>
                            <button className="flex items-center justify-center gap-1.5 py-3 bg-slate-100 text-slate-600 rounded-2xl text-[10px] font-black uppercase tracking-wide hover:bg-slate-200 transition-all"><Printer size={13} /> Imprimir</button>
                            <button onClick={() => setShowTicket(false)} className="col-span-2 py-3 bg-[var(--vet-primary)] text-white rounded-2xl text-[10px] font-black uppercase tracking-wide hover:opacity-90 transition-all">Nueva venta</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── TOP BAR ── */}
            <div className="shrink-0 bg-white border-b border-slate-100">
                <div className="flex items-center justify-between px-4 py-3 gap-3">
                    <div>
                        <h2 className="text-sm font-black text-slate-800 uppercase tracking-tight flex items-center gap-1.5">
                            <ShoppingCart size={15} className="text-[var(--vet-primary)]" /> Punto de Venta
                        </h2>
                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">
                            Hoy: {salesToday.length} venta{salesToday.length !== 1 ? 's' : ''} · {fp(totalDay)}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={() => setShowHistory(h => !h)}
                            className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-[10px] font-black uppercase tracking-wide transition-all ${showHistory ? 'bg-[var(--vet-primary)] text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>
                            <History size={12} /> Historial
                        </button>
                        {view === 'pos' && (
                            <button onClick={() => setMobileTab(t => t === 'cart' ? 'catalog' : 'cart')}
                                className="relative lg:hidden flex items-center gap-1.5 px-3 py-2 bg-[var(--vet-primary)] text-white rounded-xl text-[10px] font-black uppercase tracking-wide">
                                <ShoppingCart size={12} />
                                {cartCount > 0 && <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-orange-500 text-white text-[8px] font-black rounded-full flex items-center justify-center">{cartCount}</span>}
                                Carrito
                            </button>
                        )}
                    </div>
                </div>
                {/* Nav tabs */}
                <div className="flex border-t border-slate-100">
                    {NAV.map(n => (
                        <button key={n.id} onClick={() => setView(n.id)}
                            className={`flex-1 flex flex-col items-center py-2.5 gap-0.5 text-[9px] font-black uppercase tracking-wide transition-all border-b-2 ${view === n.id ? 'border-[var(--vet-primary)] text-[var(--vet-primary)]' : 'border-transparent text-slate-400 hover:text-slate-600'}`}>
                            <n.icon size={14} />
                            {n.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* ── HISTORIAL ── */}
            {showHistory && (
                <div className="shrink-0 bg-white border-b border-slate-100 max-h-60 overflow-y-auto px-4 py-3">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Boletas · {salesToday.length} hoy · {sales.length} total</p>
                    <div className="space-y-2">
                        {sales.slice(0, 50).map(s => (
                            <button key={s.id} onClick={() => setSelectedSale(s)}
                                className="w-full bg-slate-50 border border-slate-100 rounded-2xl p-3 flex items-center gap-3 hover:border-[var(--vet-primary)]/30 hover:shadow-sm transition-all text-left">
                                <div className="w-7 h-7 rounded-xl bg-blue-50 flex items-center justify-center shrink-0"><FileText size={13} className="text-[var(--vet-primary)]" /></div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs font-black text-slate-800 truncate">{s.clientName || 'Cliente general'} · {s.time}</p>
                                    <p className="text-[9px] text-slate-400">{s.saleNumber} · {s.date ? fdate(s.date) : 'Hoy'} · {s.docType}</p>
                                </div>
                                <div className="text-right shrink-0">
                                    <p className="text-sm font-black text-slate-800">{fp(s.total)}</p>
                                    <p className={`text-[9px] font-black uppercase ${payClr(s.payment)}`}>{payLbl(s.payment)}</p>
                                </div>
                                <ChevronRight size={11} className="text-slate-300 shrink-0" />
                            </button>
                        ))}
                        {sales.length === 0 && <p className="text-center text-xs text-slate-400 py-4">Sin boletas aún</p>}
                    </div>
                </div>
            )}

            {/* ── POS VIEW ── */}
            {view === 'pos' && (
                <div className="flex-1 flex overflow-hidden">
                    {/* CATALOG */}
                    <div className={`flex flex-col flex-1 overflow-hidden ${mobileTab === 'cart' ? 'hidden lg:flex' : 'flex'}`}>
                        <div className="shrink-0 px-3 pt-3 pb-2">
                            <div className="relative">
                                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Buscar producto o SKU…"
                                    className="w-full pl-9 pr-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[var(--vet-primary)]/30 transition-all" />
                            </div>
                        </div>
                        <div className="shrink-0 flex gap-2 px-3 pb-2 overflow-x-auto no-scrollbar">
                            {CATEGORIES.map(c => (
                                <button key={c.id} onClick={() => setCategory(c.id)}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[9px] font-black uppercase tracking-wide whitespace-nowrap transition-all shrink-0 ${category === c.id ? 'bg-[var(--vet-primary)] text-white shadow-sm' : `${c.bg} ${c.color} hover:opacity-80`}`}>
                                    <c.icon size={10} /> {c.label}
                                </button>
                            ))}
                        </div>
                        <div className="flex-1 overflow-y-auto px-3 pb-3">
                            <div className="grid grid-cols-2 lg:grid-cols-3 gap-2.5 pt-0.5">
                                {filtered.map(p => {
                                    const inCart   = cart.find(i => i.id === p.id);
                                    const lowStock = p.stock > 0 && p.stock <= 5 && p.category !== 'servicios';
                                    const def      = catDef(p.category);
                                    return (
                                        <div key={p.id} className={`bg-white border rounded-2xl p-3 flex flex-col gap-2 transition-all hover:shadow-md ${inCart ? 'border-[var(--vet-primary)]/50 shadow-sm' : 'border-slate-100'}`}>
                                            <div className="flex items-center justify-between">
                                                <span className={`text-[8px] font-black uppercase tracking-widest ${def.color}`}>{def.label}</span>
                                                {lowStock && <span className="text-[8px] font-black text-amber-500 bg-amber-50 px-1.5 py-0.5 rounded-full">Stock: {p.stock}</span>}
                                            </div>
                                            <p className="text-sm font-black text-slate-800 leading-tight flex-1">{p.name}</p>
                                            <div className="flex items-center justify-between">
                                                <p className="text-base font-black text-[var(--vet-primary)]">{fp(p.price)}</p>
                                                <p className="text-[9px] text-slate-400">/{p.unit}</p>
                                            </div>
                                            {inCart ? (
                                                <div className="flex items-center gap-1.5 bg-slate-50 rounded-xl p-1">
                                                    <button onClick={() => updateQty(p.id, -1)} className="w-7 h-7 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-red-50 hover:text-red-500 transition-all active:scale-95"><Minus size={11} /></button>
                                                    <span className="flex-1 text-center text-sm font-black text-slate-800">{inCart.qty}</span>
                                                    <button onClick={() => addToCart(p)} className="w-7 h-7 rounded-lg bg-[var(--vet-primary)] flex items-center justify-center text-white hover:opacity-90 transition-all active:scale-95"><Plus size={11} /></button>
                                                </div>
                                            ) : (
                                                <button onClick={() => addToCart(p)} disabled={p.stock === 0 && p.category !== 'servicios'}
                                                    className="w-full py-2 bg-[var(--vet-primary)] hover:opacity-90 text-white rounded-xl text-[10px] font-black uppercase tracking-wide flex items-center justify-center gap-1 transition-all active:scale-95 disabled:opacity-40">
                                                    {p.stock === 0 && p.category !== 'servicios' ? 'Sin stock' : <><Plus size={10} /> Agregar</>}
                                                </button>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                            {filtered.length === 0 && (
                                <div className="text-center py-12 text-slate-400">
                                    <Package size={32} className="mx-auto mb-3 opacity-30" />
                                    <p className="text-sm font-bold">{query ? `Sin resultados para "${query}"` : 'Sin productos'}</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* CART */}
                    <div className={`w-full lg:w-80 xl:w-96 flex flex-col bg-white border-l border-slate-100 overflow-hidden ${mobileTab === 'cart' ? 'flex' : 'hidden lg:flex'}`}>
                        {/* header */}
                        <div className="shrink-0 px-4 py-3.5 border-b border-slate-100 flex items-center justify-between">
                            <h3 className="text-sm font-black text-slate-800 uppercase tracking-tight flex items-center gap-2">
                                <ReceiptText size={15} className="text-[var(--vet-primary)]" /> Ticket
                                {cartCount > 0 && <span className="px-2 py-0.5 bg-[var(--vet-primary)] text-white text-[9px] font-black rounded-full">{cartCount}</span>}
                            </h3>
                            {cart.length > 0 && <button onClick={clearCart} className="text-[9px] font-black text-rose-400 hover:text-rose-600 uppercase tracking-wide flex items-center gap-1"><Trash2 size={10} /> Vaciar</button>}
                        </div>
                        {/* doc type + client */}
                        <div className="shrink-0 px-4 py-3 border-b border-slate-50 space-y-2">
                            <div className="flex gap-2">
                                {(['BOLETA', 'FACTURA'] as DocType[]).map(d => (
                                    <button key={d} onClick={() => setDocType(d)}
                                        className={`flex-1 py-1.5 rounded-xl text-[9px] font-black uppercase tracking-wide transition-all ${docType === d ? 'bg-[var(--vet-primary)] text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>
                                        {d}
                                    </button>
                                ))}
                            </div>
                            <div className="relative">
                                <User size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                <input value={client} onChange={e => setClient(e.target.value)} placeholder="Cliente / paciente (opcional)"
                                    className="w-full pl-8 pr-3 py-2 bg-slate-50 border border-slate-100 rounded-xl text-xs font-medium text-slate-700 placeholder:text-slate-400 focus:outline-none focus:border-[var(--vet-primary)]/50 transition-all" />
                            </div>
                            {docType === 'FACTURA' && (
                                <input value={clientRut} onChange={e => setClientRut(e.target.value)} placeholder="RUT empresa (12.345.678-9)"
                                    className="w-full px-3 py-2 bg-slate-50 border border-slate-100 rounded-xl text-xs font-medium text-slate-700 placeholder:text-slate-400 focus:outline-none focus:border-[var(--vet-primary)]/50 transition-all" />
                            )}
                        </div>
                        {/* items */}
                        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
                            {cart.length === 0 ? (
                                <div className="flex flex-col items-center justify-center h-full text-center gap-3 py-12">
                                    <ShoppingCart size={32} strokeWidth={1.5} className="text-slate-300" />
                                    <p className="text-xs font-bold text-slate-400">Carrito vacío.<br />Agrega productos del catálogo.</p>
                                </div>
                            ) : cart.map(item => (
                                <div key={item.id} className="flex items-start gap-3 bg-slate-50 rounded-2xl p-3">
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs font-black text-slate-800 leading-tight">{item.name}</p>
                                        <p className={`text-[9px] font-black uppercase ${catDef(item.category).color}`}>{catDef(item.category).label}</p>
                                        <p className="text-xs font-bold text-slate-500 mt-1">{fp(item.price)} × {item.qty} = <span className="text-slate-800 font-black">{fp(item.price * item.qty)}</span></p>
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0">
                                        <button onClick={() => updateQty(item.id, -1)} className="w-6 h-6 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-red-50 hover:text-red-400 transition-all active:scale-90"><Minus size={9} /></button>
                                        <span className="w-6 text-center text-xs font-black text-slate-800">{item.qty}</span>
                                        <button onClick={() => addToCart(item)} className="w-6 h-6 rounded-lg bg-[var(--vet-primary)] flex items-center justify-center text-white hover:opacity-80 transition-all active:scale-90"><Plus size={9} /></button>
                                        <button onClick={() => removeItem(item.id)} className="w-6 h-6 rounded-lg bg-rose-50 flex items-center justify-center text-rose-400 hover:bg-rose-100 transition-all active:scale-90 ml-1"><X size={9} /></button>
                                    </div>
                                </div>
                            ))}
                        </div>
                        {/* totals + payment */}
                        <div className="shrink-0 border-t border-slate-100 px-4 py-4 space-y-3">
                            {/* discount */}
                            <div className="flex items-center gap-2">
                                <Tag size={11} className="text-slate-400 shrink-0" />
                                <span className="text-[9px] font-black text-slate-500 uppercase tracking-wide shrink-0">Dto.</span>
                                <div className="flex items-center gap-1 ml-auto">
                                    {[0, 5, 10, 15, 20].map(d => (
                                        <button key={d} onClick={() => setDiscount(d)} className={`px-2 py-1 rounded-lg text-[9px] font-black transition-all ${discount === d ? 'bg-[var(--vet-primary)] text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>{d}%</button>
                                    ))}
                                </div>
                            </div>
                            {/* summary */}
                            <div className="bg-slate-50 rounded-2xl p-3 space-y-1.5">
                                <div className="flex justify-between text-xs text-slate-500"><span>Subtotal</span><span className="font-bold">{fp(subtotal)}</span></div>
                                {discount > 0 && <div className="flex justify-between text-xs text-emerald-600"><span>Dto. {discount}%</span><span className="font-bold">-{fp(discountAmt)}</span></div>}
                                <div className="flex justify-between text-sm font-black text-slate-800 pt-1.5 border-t border-slate-200"><span>TOTAL</span><span>{fp(total)}</span></div>
                            </div>
                            {/* payment */}
                            <div>
                                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2">Método de pago</p>
                                <div className="grid grid-cols-2 gap-2">
                                    {PAY_METHODS.map(m => (
                                        <button key={m.id} onClick={() => setPayment(m.id)}
                                            className={`flex items-center gap-1.5 py-2.5 px-3 rounded-xl text-[10px] font-black uppercase tracking-wide transition-all border-2 ${payment === m.id ? `${m.color} text-white border-transparent shadow-md` : 'bg-slate-50 text-slate-500 border-slate-100 hover:border-slate-200'}`}>
                                            <m.icon size={11} /> {m.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            {(payment === 'debito' || payment === 'credito') && (
                                <p className="text-[9px] text-slate-400 font-bold text-center">
                                    Abre selector de terminal físico (Tuu / MercadoPago / Transbank)
                                </p>
                            )}
                            {/* complete */}
                            <button onClick={handleCompleteSale} disabled={cart.length === 0 || !payment}
                                className="w-full py-3.5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-2xl text-sm font-black uppercase tracking-wide flex items-center justify-center gap-2 transition-all active:scale-[0.98] shadow-lg shadow-emerald-500/20">
                                <Check size={16} />
                                {payment === 'efectivo' ? 'Calcular vuelto' : payment === 'debito' || payment === 'credito' ? 'Pagar con terminal' : 'Completar venta'}
                                {total > 0 && ` · ${fp(total)}`}
                            </button>
                            {(cart.length === 0 || !payment) && <p className="text-center text-[9px] text-slate-400 font-bold">{cart.length === 0 ? 'Agrega productos para continuar' : 'Selecciona método de pago'}</p>}
                        </div>
                    </div>
                </div>
            )}

            {/* ── MANAGE VIEW ── */}
            {view === 'manage' && (() => {
                const lowStockItems = products.filter(p => p.category !== 'servicios' && p.stock !== undefined && (p.stock_alert_threshold !== undefined ? p.stock <= p.stock_alert_threshold : p.stock <= 5));
                return (
                <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Catálogo POS</p>
                            <h3 className="text-base font-black text-slate-800">Gestión de Productos</h3>
                            <p className="text-xs text-slate-400">{products.length} productos</p>
                        </div>
                        <button onClick={() => { setEditingProd(null); setShowProdForm(true); }}
                            className="flex items-center gap-1.5 px-4 py-2.5 bg-[var(--vet-primary)] text-white rounded-xl text-[10px] font-black uppercase tracking-wide hover:opacity-90 transition-all">
                            <Plus size={12} /> Nuevo
                        </button>
                    </div>

                    {/* Low stock alert panel */}
                    {lowStockItems.length > 0 && (
                        <div className="bg-rose-50 border border-rose-200 rounded-2xl p-4">
                            <div className="flex items-center gap-2 mb-3">
                                <AlertCircle size={14} className="text-rose-500 shrink-0" />
                                <p className="text-xs font-black text-rose-700 uppercase tracking-wide">⚠ {lowStockItems.length} producto{lowStockItems.length !== 1 ? 's' : ''} con stock bajo</p>
                            </div>
                            <div className="space-y-1.5">
                                {lowStockItems.map(p => (
                                    <div key={p.id} className="flex items-center justify-between bg-white rounded-xl px-3 py-2">
                                        <span className="text-xs font-bold text-slate-700 truncate">{p.name}</span>
                                        <span className="text-[10px] font-black text-rose-600 shrink-0 ml-2">
                                            {p.stock} {p.unit} restante{p.stock !== 1 ? 's' : ''}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
                        {CATEGORIES.map(c => (
                            <button key={c.id} onClick={() => setManCat(c.id)}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[9px] font-black uppercase tracking-wide whitespace-nowrap shrink-0 transition-all ${manCat === c.id ? 'bg-[var(--vet-primary)] text-white' : `${c.bg} ${c.color} hover:opacity-80`}`}>
                                <c.icon size={10} /> {c.label}
                            </button>
                        ))}
                    </div>
                    <div className="space-y-2">
                        {manageList.map(p => {
                            const threshold = p.stock_alert_threshold ?? 5;
                            const isLow = p.category !== 'servicios' && p.stock <= threshold;
                            return (
                            <div key={p.id} className={`bg-white border rounded-2xl p-4 flex items-center gap-4 hover:shadow-sm transition-all ${isLow ? 'border-rose-200 bg-rose-50/30' : 'border-slate-100'}`}>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                                        <p className="text-sm font-black text-slate-800 truncate">{p.name}</p>
                                        <span className={`text-[8px] font-black uppercase tracking-wider shrink-0 ${catDef(p.category).color}`}>{catDef(p.category).label}</span>
                                        {isLow && <span className="text-[8px] font-black text-rose-600 bg-rose-100 px-1.5 py-0.5 rounded-full uppercase shrink-0">⚠ Stock Bajo</span>}
                                    </div>
                                    <div className="flex items-center gap-3 text-[10px] font-medium flex-wrap">
                                        <span className="font-black text-slate-700">{fp(p.price)}</span>
                                        <span className="text-slate-400">/{p.unit}</span>
                                        {p.sku && <span className="bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-lg font-mono">{p.sku}</span>}
                                        {p.category !== 'servicios' && (
                                            <span className={`font-black ${isLow ? 'text-rose-500' : 'text-slate-400'}`}>
                                                Stock: {p.stock} / Alerta: ≤{threshold}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                    <button onClick={() => { setEditingProd(p); setShowProdForm(true); }} className="w-8 h-8 rounded-xl bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-[var(--vet-primary)]/10 hover:text-[var(--vet-primary)] transition-all"><Edit2 size={13} /></button>
                                    <button onClick={() => handleDelProd(p)} className="w-8 h-8 rounded-xl bg-rose-50 flex items-center justify-center text-rose-400 hover:bg-rose-100 transition-all"><Trash2 size={13} /></button>
                                </div>
                            </div>
                            );
                        })}
                        {manageList.length === 0 && <div className="text-center py-12 text-slate-400"><Package size={32} className="mx-auto mb-3 opacity-30" /><p className="text-sm font-bold">Sin productos</p></div>}
                    </div>
                </div>
                );
            })()}

            {/* ── CAJA VIEW ── */}
            {view === 'caja' && (
                <CajaView
                    sales={sales}
                    session={cajaSession}
                    onOpenCaja={handleOpenCaja}
                    onCloseCaja={handleCloseCaja}
                />
            )}

            {/* ── REPORTES VIEW ── */}
            {view === 'reportes' && (
                <ReportesView
                    sales={sales}
                    termConfig={termConfig}
                    onSaveTermConfig={c => { setTermConfig(c); ls.set(SK.term, c); }}
                />
            )}

            {/* Mobile: back to catalog */}
            {view === 'pos' && mobileTab === 'cart' && (
                <div className="lg:hidden shrink-0 px-4 pt-2 bg-white border-t border-slate-100">
                    <button onClick={() => setMobileTab('catalog')} className="w-full py-2.5 bg-slate-100 text-slate-600 rounded-xl text-[10px] font-black uppercase tracking-wide">← Volver al catálogo</button>
                </div>
            )}
        </div>
    );
};

export default VetPOS;
